import { ENV } from "@/config/environment";
import {
  RBAC_MODULE_CATALOG,
  type ModuleGrant,
  type RbacLevel,
} from "@/config/rbac";
import {
  applyTokenResponse,
  authenticatedFetch,
  getCurrentAccessToken,
  getCurrentTenantId,
  getCurrentUser,
  type AuthenticatedFetchOptions,
} from "@/services/auth";
import type { CreationOptionsJSON } from "@/services/webauthnCodec";
import type {
  ApiResponse,
  PagedResponse,
  TokenResponse,
  UserProfile,
  Tenant,
  TenantStatus,
  TenantMember,
  Client,
  ClientRequest,
  CreateTenantRequest,
  UpdateTenantRequest,
  InviteMemberRequest,
  UpdateMemberRequest,
  AiChatResponse,
  Project,
  MatterDeadline,
  MatterDeadlineRequest,
  MatterDetail,
  MatterSummary,
  MatterParty,
  MatterPartyRequest,
  MatterTemplate,
  MatterTemplatePayload,
  ProjectAiContext,
  CreateProjectRequest,
  UpdateProjectRequest,
  CompanyProfile,
  UpdateCompanyProfileRequest,
  TeamMember,
  ProjectDetailMetrics,
  TenantProjectMetrics,
  DocumentSummary,
  DocumentContent,
  DocumentDiff,
  DocumentHistoryEvent,
  DocumentRagStatus,
  ReviewerSummary,
  DocumentVersion,
  DownloadUrl,
  DocumentMetrics,
  DocumentType,
  DocumentClassifyResponse,
} from "@/types/api";

const BASE = ENV.backendUrl;
const CROSS_TENANT: AuthenticatedFetchOptions = { tenantIdOverride: "*" };

export class BackendError extends Error {
  constructor(
    message: string,
    public code?: string,
    public status?: number,
  ) {
    super(message);
  }
}

export async function call<T>(
  path: string,
  init?: RequestInit,
  opts?: AuthenticatedFetchOptions,
): Promise<T> {
  const res = await authenticatedFetch(`${BASE}${path}`, init, opts);
  if (res.status === 204) return null as T;
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.success) {
    throw new BackendError(
      json.error ?? "unknown error",
      json.code,
      res.status,
    );
  }
  return json.data as T;
}

export function jsonBody(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}
export function putBody(body: unknown): RequestInit {
  return {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}
export function patchBody(body: unknown): RequestInit {
  return {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}
export function del(): RequestInit {
  return { method: "DELETE" };
}

export interface MultipartUploadOptions {
  onProgress?: (progress: number, loaded: number, total: number) => void;
}

export async function uploadMultipart<T>(
  path: string,
  formData: FormData,
  options: MultipartUploadOptions = {},
): Promise<T> {
  const token = await getCurrentAccessToken();
  const tenantId = getCurrentTenantId();
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}${path}`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    if (tenantId) xhr.setRequestHeader("X-Tenant-Id", tenantId);
    xhr.responseType = "text";
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !options.onProgress) return;
      options.onProgress(
        Math.round((event.loaded / event.total) * 100),
        event.loaded,
        event.total,
      );
    };
    xhr.onload = () => {
      const raw = xhr.responseText;
      if (xhr.status === 413) {
        reject(
          new BackendError(
            "El archivo supera el límite de 50 MB",
            "FILE_TOO_LARGE",
            xhr.status,
          ),
        );
        return;
      }
      if (xhr.status === 204) {
        resolve(null as T);
        return;
      }
      let json: ApiResponse<T> | null = null;
      try {
        json = raw ? (JSON.parse(raw) as ApiResponse<T>) : null;
      } catch {
        reject(
          new BackendError(
            "Respuesta inválida del backend",
            "INVALID_RESPONSE",
            xhr.status,
          ),
        );
        return;
      }
      if (!json?.success) {
        reject(
          new BackendError(
            json?.error ?? "upload failed",
            json?.code,
            xhr.status,
          ),
        );
        return;
      }
      resolve(json.data as T);
    };
    xhr.onerror = () =>
      reject(
        new BackendError(
          "No se pudo subir el archivo",
          "NETWORK_ERROR",
          xhr.status,
        ),
      );
    xhr.onabort = () =>
      reject(new BackendError("Carga cancelada", "UPLOAD_ABORTED", xhr.status));
    xhr.send(formData);
  });
}

function emptyPage<T>(page = 0, size = 50): PagedResponse<T> {
  return {
    content: [],
    page,
    size,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
  };
}

function permissionFromCode(code: string): Permission {
  const [module = "system"] = code.split(".");
  return {
    id: code,
    code,
    module,
    description: code,
  };
}

export type RbacPermissionLevel = "super" | "full" | "view" | "none";

function fallbackPermissions(module?: string): Permission[] {
  const permissions = getCurrentUser()?.permissions ?? [];
  return permissions
    .filter((code) => !module || code.startsWith(`${module}.`))
    .map(permissionFromCode);
}

function fallbackRoles(): Role[] {
  const user = getCurrentUser();
  const permissions = user?.permissions ?? [];
  return (user?.roles?.length ? user.roles : ["USER"]).map((role) => ({
    id: role,
    name: role,
    displayName: null,
    category: (role === "SUPERADMIN" || role === "USER"
      ? "SYSTEM"
      : "BUSINESS") as Role["category"],
    description:
      role === "SUPERADMIN"
        ? "Rol maestro del producto"
        : "Rol del usuario actual",
    isSystem: role === "SUPERADMIN",
    tenantId: null,
    isActive: true,
    permissionCount: permissions.length,
    userCount: role === "SUPERADMIN" ? 1 : 0,
    permissions: role === "SUPERADMIN" ? permissions : [],
  }));
}

function stringifyConfigValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  return typeof value === "string" ? value : JSON.stringify(value);
}

function parseConfigValue(value: string, valueType: string): unknown {
  switch (valueType) {
    case "BOOLEAN":
      return value === "true";
    case "NUMBER":
      return Number(value);
    case "JSON":
      return JSON.parse(value);
    default:
      return value;
  }
}

function mapTenant(t: Tenant): Tenant {
  const rawCount = (t as Tenant & { membersCount?: unknown }).membersCount;
  const parsedCount = Number(rawCount);
  return {
    ...t,
    membersCount:
      rawCount === undefined || rawCount === null
        ? -1
        : Number.isFinite(parsedCount)
          ? parsedCount
          : -1,
  };
}

type TenantMemberDto = TenantMember & {
  owner?: boolean;
  isOwner?: boolean;
  userEmail?: string;
  fullName?: string | null;
};

function mapTenantMember(m: TenantMemberDto): TenantMember {
  return {
    ...m,
    email: m.email ?? m.userEmail ?? "",
    fullName: m.fullName ?? "",
    isOwner: Boolean(m.isOwner ?? m.owner),
  };
}

// ============ Auth ============
export const authApi = {
  me: () => call<UserProfile>("/auth/me"),
};

// ============ Tenants ============
export interface TenantListParams {
  page?: number;
  size?: number;
  q?: string;
  status?: TenantStatus;
  sort?: string;
}

export function buildQuery(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/**
 * Endpoints de gestión de tenants. Las llamadas admin-only usan
 * `X-Tenant-Id: *` para operar
 * cross-tenant (lo permite el backend sólo para SUPERADMIN).
 */
export const tenantsApi = {
  list: (params: TenantListParams = {}) => {
    const { page = 0, size = 50, q, status, sort } = params;
    return call<PagedResponse<Tenant>>(
      `/tenants${buildQuery({ page, size, q, status, sort })}`,
      undefined,
      CROSS_TENANT,
    ).then((result) => ({ ...result, content: result.content.map(mapTenant) }));
  },
  get: (id: string) =>
    call<Tenant>(`/tenants/${id}`, undefined, CROSS_TENANT).then(mapTenant),
  getScoped: (id: string) => call<Tenant>(`/tenants/${id}`).then(mapTenant),
  create: (req: CreateTenantRequest) =>
    call<Tenant>("/tenants", jsonBody(req), CROSS_TENANT).then(mapTenant),
  update: (id: string, req: UpdateTenantRequest) =>
    call<Tenant>(`/tenants/${id}`, putBody(req), CROSS_TENANT).then(mapTenant),
  remove: (id: string) => call<void>(`/tenants/${id}`, del(), CROSS_TENANT),

  members: {
    list: (
      tenantId: string,
      params: { page?: number; size?: number; q?: string } = {},
    ) => {
      const { page = 0, size = 50, q } = params;
      return call<PagedResponse<TenantMemberDto>>(
        `/tenants/${tenantId}/members${buildQuery({ page, size, q })}`,
        undefined,
        { tenantIdOverride: tenantId },
      ).then((page) => ({
        ...page,
        content: page.content.map(mapTenantMember),
      }));
    },
    invite: (tenantId: string, req: InviteMemberRequest) =>
      call<TenantMemberDto>(`/tenants/${tenantId}/members`, jsonBody(req), {
        tenantIdOverride: tenantId,
      }).then(mapTenantMember),
    update: (tenantId: string, userId: string, req: UpdateMemberRequest) =>
      call<TenantMemberDto>(
        `/tenants/${tenantId}/members/${userId}`,
        putBody(req),
        { tenantIdOverride: tenantId },
      ).then(mapTenantMember),
    remove: (tenantId: string, userId: string) =>
      call<void>(`/tenants/${tenantId}/members/${userId}`, del(), {
        tenantIdOverride: tenantId,
      }),
  },

  // SUPERADMIN cross-tenant moderation
  admin: {
    listAll: (params: { page?: number; size?: number; q?: string } = {}) => {
      const { page = 0, size = 50, q } = params;
      return call<PagedResponse<Tenant>>(
        `/admin/tenants${buildQuery({ page, size, q })}`,
        undefined,
        CROSS_TENANT,
      ).then((result) => ({
        ...result,
        content: result.content.map(mapTenant),
      }));
    },
    suspend: (id: string) =>
      call<void>(
        `/admin/tenants/${id}/suspend`,
        { method: "POST" },
        CROSS_TENANT,
      ),
    reactivate: (id: string) =>
      call<void>(
        `/admin/tenants/${id}/reactivate`,
        { method: "POST" },
        CROSS_TENANT,
      ),
  },
};

// ============ Projects ============
export interface ProjectListParams {
  page?: number;
  size?: number;
  q?: string;
  search?: string;
  status?: Project["status"] | "ALL";
  clientId?: string;
  companyProfileId?: string;
  counterpartyId?: string;
  matterType?: string;
  matterTypeId?: string;
  assignee?: string;
  includeArchived?: boolean;
  /** ISO instant: filtra matters creados desde esta fecha. */
  createdFrom?: string;
  /** ISO instant: filtra matters creados hasta esta fecha. */
  createdTo?: string;
}

export const projectsApi = {
  list: (
    pageOrParams: number | ProjectListParams = 0,
    size = 20,
    q?: string,
  ): Promise<PagedResponse<Project>> => {
    const params: ProjectListParams =
      typeof pageOrParams === "number"
        ? { page: pageOrParams, size, q }
        : pageOrParams;
    if (params.clientId) {
      return call<PagedResponse<MatterSummary>>(
        `/matters${buildQuery({
          page: params.page ?? 0,
          size: params.size ?? 20,
          q: params.q ?? params.search,
          clientId: params.clientId,
          includeArchived: params.includeArchived,
        })}`,
      ).then((page) => {
        const content = page.content.map(projectFromMatterSummary);
        return {
          ...page,
          content,
          items: page.items?.map(projectFromMatterSummary),
        };
      });
    }
    const status =
      params.status && params.status !== "ALL" ? params.status : undefined;
    return call<PagedResponse<Project>>(
      `/projects${buildQuery({
        page: params.page ?? 0,
        size: params.size ?? 20,
        q: params.q ?? params.search,
        status,
        clientId: params.clientId,
        counterpartyId: params.counterpartyId,
        matterType: params.matterType,
        matterTypeId: params.matterTypeId,
        createdFrom: params.createdFrom,
        createdTo: params.createdTo,
        includeArchived: params.includeArchived,
      })}`,
    );
  },
  metrics: () => call<TenantProjectMetrics>("/projects/metrics"),
  projectMetrics: (id: string) =>
    call<ProjectDetailMetrics>(`/projects/${id}/metrics`),
  get: (id: string) => call<Project>(`/projects/${id}`),
  aiContext: (id: string) =>
    call<ProjectAiContext>(`/projects/${id}/ai/context`),
  aiChat: (
    id: string,
    body: {
      message: string;
      conversationId?: string | null;
      includeJurisprudencia?: boolean;
      /** Limita el contexto RAG a estos documentos del matter (vacío/ausente = todo el matter). */
      documentIds?: string[];
      /** Id de modelo IA preferido (de GET /ai/models). */
      model?: string;
    },
  ) => call<AiChatResponse>(`/projects/${id}/ai/chat`, jsonBody(body)),
  create: (req: CreateProjectRequest) =>
    call<Project>("/projects", jsonBody(req)),
  update: (id: string, req: UpdateProjectRequest) =>
    call<Project>(`/projects/${id}`, putBody(req)),
  transition: (id: string, to: Project["status"]) =>
    call<Project>(`/projects/${id}/transition`, jsonBody({ to })),
  setPinned: (id: string, pinned: boolean) =>
    call<Project>(`/projects/${id}/pin`, putBody({ pinned })),
  setTags: (id: string, tags: string[]) =>
    call<Project>(`/projects/${id}/tags`, putBody({ tags })),
  remove: (id: string) => call<void>(`/projects/${id}`, del()),

  getCompanyProfile: (id: string) =>
    call<CompanyProfile>(`/projects/${id}/company-profile`),
  upsertCompanyProfile: (id: string, req: UpdateCompanyProfileRequest) =>
    call<CompanyProfile>(`/projects/${id}/company-profile`, putBody(req)),

  listTeam: (id: string) => call<TeamMember[]>(`/projects/${id}/team`),
  replaceTeam: (id: string, members: Pick<TeamMember, "userId" | "role">[]) =>
    call<TeamMember[]>(`/projects/${id}/team`, putBody({ members })),
  addTeam: (
    id: string,
    req: { userId: string; userEmail: string; role: TeamMember["role"] },
  ) => call<TeamMember>(`/projects/${id}/team`, jsonBody(req)),
  removeTeam: (id: string, memberId: string) =>
    call<void>(`/projects/${id}/team/${memberId}`, del()),

  reminderSettings: (tenantId: string) =>
    call<ProjectReminderSettings>(
      `/tenants/${tenantId}/projects/reminders/settings`,
      undefined,
      { tenantIdOverride: tenantId },
    ),
  updateReminderSettings: (tenantId: string, body: ProjectReminderSettings) =>
    call<ProjectReminderSettings>(
      `/tenants/${tenantId}/projects/reminders/settings`,
      putBody(body),
      { tenantIdOverride: tenantId },
    ),
};

// ============ Clients / Matters ============
export interface ClientListParams {
  page?: number;
  size?: number;
  q?: string;
  activeOnly?: boolean;
}

export const clientsApi = {
  list: (params: ClientListParams = {}) =>
    call<PagedResponse<Client>>(
      `/clients${buildQuery({
        page: params.page ?? 0,
        size: params.size ?? 50,
        q: params.q,
        active: params.activeOnly ?? true,
      })}`,
    ),
  get: (id: string) => call<Client>(`/clients/${id}`),
  create: (body: ClientRequest) => call<Client>("/clients", jsonBody(body)),
  update: (id: string, body: Partial<ClientRequest>) =>
    call<Client>(`/clients/${id}`, putBody(body)),
  remove: (id: string) => call<void>(`/clients/${id}`, del()),
};

function projectFromMatter(detail: MatterDetail): Project {
  return {
    id: detail.summary.id,
    tenantId: detail.summary.tenantId,
    name: detail.summary.name,
    description: detail.description,
    clientId: detail.summary.clientId,
    matterNumber: detail.summary.matterNumber,
    agreementType: detail.agreementType,
    industry: detail.industry,
    jurisdiction: detail.jurisdiction,
    counterparty: null,
    counterpartyId: null,
    counterpartyDetails: null,
    matterTypeId: detail.matterTypeId,
    matterTypeCode: null,
    matterTypeName: null,
    matterTypeColorHex: null,
    matterTypeIcon: null,
    estimatedAmountUsd: null,
    criticalDate: detail.criticalDate,
    status: detail.summary.status,
    teamSize: 0,
    hasCompanyProfile: false,
    active: detail.summary.active,
    isActive: detail.summary.active,
    createdAt: detail.summary.createdAt,
    updatedAt: detail.summary.updatedAt,
  };
}

function projectFromMatterSummary(summary: MatterSummary): Project {
  return {
    id: summary.id,
    tenantId: summary.tenantId,
    name: summary.name,
    description: null,
    clientId: summary.clientId,
    matterNumber: summary.matterNumber,
    agreementType: null,
    industry: null,
    jurisdiction: null,
    counterparty: null,
    counterpartyId: null,
    counterpartyDetails: null,
    matterTypeId: null,
    matterTypeCode: null,
    matterTypeName: null,
    matterTypeColorHex: null,
    matterTypeIcon: null,
    estimatedAmountUsd: null,
    criticalDate: null,
    status: summary.status,
    teamSize: 0,
    hasCompanyProfile: false,
    riskScore: summary.riskScore,
    riskLevel: summary.riskLevel,
    active: summary.active,
    isActive: summary.active,
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
  };
}

export const mattersApi = {
  list: (params: ProjectListParams = {}) =>
    call<PagedResponse<MatterDetail["summary"]>>(
      `/matters${buildQuery({
        page: params.page ?? 0,
        size: params.size ?? 20,
        q: params.q ?? params.search,
        clientId: params.clientId,
        includeArchived: params.includeArchived,
      })}`,
    ),
  get: (id: string) => call<MatterDetail>(`/matters/${id}`),
  create: (req: CreateProjectRequest, template?: string) =>
    call<MatterDetail>(
      `/matters${buildQuery({ template })}`,
      jsonBody(req),
    ).then(projectFromMatter),
  transition: (id: string, to: Project["status"]) =>
    call<MatterDetail>(`/matters/${id}/transition`, jsonBody({ to })),
  templates: (
    params?:
      | string
      | {
          practiceArea?: string;
          q?: string;
          includeInactive?: boolean;
        },
  ) => {
    const query =
      typeof params === "string" ? { practiceArea: params } : params;
    return call<MatterTemplate[]>(
      `/matters/templates${buildQuery(query ?? {})}`,
    );
  },
  template: (id: string) => call<MatterTemplate>(`/matters/templates/${id}`),
  createTemplate: (
    body: MatterTemplatePayload,
    opts?: AuthenticatedFetchOptions,
  ) => call<MatterTemplate>(`/matters/templates`, jsonBody(body), opts),
  updateTemplate: (
    id: string,
    body: MatterTemplatePayload,
    opts?: AuthenticatedFetchOptions,
  ) => call<MatterTemplate>(`/matters/templates/${id}`, putBody(body), opts),
  archiveTemplate: (id: string, opts?: AuthenticatedFetchOptions) =>
    call<MatterTemplate>(`/matters/templates/${id}`, del(), opts),
  parties: (id: string) => call<MatterParty[]>(`/matters/${id}/parties`),
  addParty: (id: string, body: MatterPartyRequest) =>
    call<MatterParty>(`/matters/${id}/parties`, jsonBody(body)),
  deadlines: (id: string) => call<MatterDeadline[]>(`/matters/${id}/deadlines`),
  createDeadline: (id: string, body: MatterDeadlineRequest) =>
    call<MatterDeadline>(`/matters/${id}/deadlines`, jsonBody(body)),
  updateDeadlineStatus: (
    id: string,
    deadlineId: string,
    status: MatterDeadline["status"],
  ) =>
    call<MatterDeadline>(
      `/matters/${id}/deadlines/${deadlineId}`,
      patchBody({ status }),
    ),
  declareWall: (
    id: string,
    body: {
      conflictGroup: string;
      blockedUserIds?: string[];
      allowedUserIds?: string[];
      reason?: string;
    },
  ) => call<unknown>(`/matters/${id}/conflict-wall`, jsonBody(body)),
};

// ============ Documents ============
export interface DocumentListParams {
  page?: number;
  size?: number;
  q?: string;
  projectId?: string;
  status?: DocumentSummary["status"] | "ALL";
  type?: string;
}

export interface DocumentDraftSaveResponse {
  documentId: string;
  savedAt: string;
  contentHash: string;
}

// ============ Aportes externos del documento (share links) ============
// Comentarios/aprobaciones creados desde el visor público "compartir por enlace"
// y expuestos al usuario autenticado del editor (batch3). Tipos locales TS:
// se reemplazarán por los generados con `openapi-typescript` al regenerar el
// contrato en host.

/** Tipo de enlace que originó el aporte externo. */
export type DocumentShareLinkType = "VIEW" | "COMMENT" | "APPROVE";

/** Tipo de aporte externo: comentario libre o sugerencia de cambio. */
export type DocumentShareCommentType = "COMMENT" | "SUGGESTION";

/** Comentario/sugerencia externo (GET /documents/{id}/share/comments). */
export interface DocumentShareComment {
  id: string;
  documentId: string;
  shareLinkId: string;
  linkType: DocumentShareLinkType;
  authorLabel: string;
  text: string;
  commentType: DocumentShareCommentType;
  quote?: string | null;
  selectionStart?: number | null;
  selectionEnd?: number | null;
  suggestedText?: string | null;
  createdAt: string;
}

export type DocumentShareApprovalDecision = "APPROVED" | "REJECTED";

/** Decisión de aprobación externa (GET /documents/{id}/share/approvals). */
export interface DocumentShareApproval {
  id: string;
  documentId: string;
  shareLinkId: string;
  linkType: DocumentShareLinkType;
  decision: DocumentShareApprovalDecision;
  comment?: string | null;
  authorLabel: string;
  decidedAt: string;
}

export const documentsApi = {
  list: (params: DocumentListParams = {}) => {
    const status =
      params.status && params.status !== "ALL" ? params.status : undefined;
    return call<PagedResponse<DocumentSummary>>(
      `/documents${buildQuery({
        page: params.page ?? 0,
        size: params.size ?? 20,
        search: params.q,
        projectId: params.projectId,
        status,
        type: params.type,
      })}`,
    );
  },
  listByProject: (projectId: string, page = 0, size = 20) =>
    documentsApi.list({ projectId, page, size }),
  metrics: () => call<DocumentMetrics>("/documents/metrics"),
  get: (id: string) => call<DocumentSummary>(`/documents/${id}`),
  ragStatus: (id: string) =>
    call<DocumentRagStatus>(`/documents/${id}/rag-status`),
  /**
   * Re-encola la indexación RAG del documento (S42). El backend responde 422
   * con code DOCUMENT_NO_TEXT si el documento no tiene texto extraído.
   */
  ragReindex: (id: string) =>
    call<DocumentRagStatus>(`/documents/${id}/rag-reindex`, jsonBody({})),
  reviewers: (id: string) =>
    call<ReviewerSummary[]>(`/documents/${id}/reviewers`),
  versions: (id: string) =>
    call<DocumentVersion[]>(`/documents/${id}/versions`),
  diff: (id: string, fromVersion: number, toVersion: number) =>
    call<DocumentDiff>(
      `/documents/${id}/diff?from=${fromVersion}&to=${toVersion}`,
    ),
  history: (
    id: string,
    params: { page?: number; size?: number; types?: string[] } = {},
  ) =>
    call<PagedResponse<DocumentHistoryEvent>>(
      `/documents/${id}/history${buildQuery({
        page: params.page ?? 0,
        size: params.size ?? 20,
        types: params.types?.join(","),
      })}`,
    ),
  downloadUrl: (id: string, version?: number) =>
    call<DownloadUrl>(
      `/documents/${id}/download${version ? `?version=${version}` : ""}`,
    ),
  downloadVersionUrl: (id: string, versionId: string) =>
    call<DownloadUrl>(`/documents/${id}/versions/${versionId}/download`),

  getContent: (id: string, version?: number) =>
    call<DocumentContent>(
      `/documents/${id}/content${version ? `?version=${version}` : ""}`,
    ),
  saveContent: (id: string, contentHtml: string, comment?: string) =>
    call<DocumentVersion>(
      `/documents/${id}/content`,
      jsonBody({ contentHtml, comment }),
    ),
  saveDraft: (id: string, contentHtml: string) =>
    call<DocumentDraftSaveResponse>(
      `/documents/${id}/draft`,
      patchBody({ contentHtml }),
    ),
  remove: (id: string) => call<void>(`/documents/${id}`, del()),
  documentTypes: () => call<DocumentType[]>("/document-types"),
  classify: (id: string) =>
    call<DocumentClassifyResponse>(`/documents/${id}/classify`, jsonBody({})),
  overrideType: (id: string, documentTypeId: string) =>
    call<DocumentClassifyResponse>(
      `/documents/${id}/type`,
      patchBody({ documentTypeId }),
    ),

  /**
   * Crea documento y sube primera versión. Multipart con dos partes: `metadata` (JSON) y `file`.
   */
  async create(
    metadata: { projectId: string; name: string; description?: string },
    file: File,
    comment?: string,
    onProgress?: MultipartUploadOptions["onProgress"],
  ): Promise<DocumentSummary> {
    const form = new FormData();
    form.append("metadataJson", JSON.stringify(metadata));
    form.append("file", file);
    if (comment) form.append("comment", comment);
    return uploadMultipart<DocumentSummary>("/documents", form, { onProgress });
  },

  /**
   * Sube una versión nueva a un documento existente.
   */
  async uploadVersion(
    id: string,
    file: File,
    comment?: string,
    onProgress?: MultipartUploadOptions["onProgress"],
  ): Promise<DocumentVersion> {
    const form = new FormData();
    form.append("file", file);
    if (comment) {
      form.append("comment", comment);
      form.append("notes", comment);
    }
    return uploadMultipart<DocumentVersion>(
      `/documents/${id}/versions${comment ? `?comment=${encodeURIComponent(comment)}` : ""}`,
      form,
      { onProgress },
    );
  },

  /**
   * Comentarios/sugerencias externos creados desde enlaces de "compartir por
   * enlace" (links COMMENT/APPROVE). Para el usuario autenticado del editor.
   */
  shareComments: (id: string) =>
    call<DocumentShareComment[]>(`/documents/${id}/share/comments`),

  /**
   * Aprobaciones externas (decisiones APPROVED/REJECTED) registradas desde
   * enlaces de aprobación. Para el usuario autenticado del editor.
   */
  shareApprovals: (id: string) =>
    call<DocumentShareApproval[]>(`/documents/${id}/share/approvals`),
};

// ============ Admin · RBAC ============
export interface Permission {
  id: string;
  code: string;
  module: string;
  description: string;
}
export type RoleCategory = "SYSTEM" | "BUSINESS";

export interface Role {
  id: string;
  /** Code técnico (no se renombra; hay checks por nombre que dependen de él). */
  name: string;
  /** Nombre visible en español (S46 Wave A0). Fallback: `name`. */
  displayName: string | null;
  category: RoleCategory;
  description: string;
  isSystem: boolean;
  tenantId: string | null;
  isActive: boolean;
  permissions: string[];
  permissionCount?: number;
  userCount?: number;
  icon?: string;
}

/** Label visible de un rol: displayName en español con fallback al code. */
export function roleLabel(role: Pick<Role, "name" | "displayName">): string {
  return role.displayName?.trim() ? role.displayName : role.name;
}

/** DTO crudo del backend; tolera el campo legacy `system` pre-S46. */
interface RoleDto extends Omit<
  Role,
  "isSystem" | "displayName" | "category" | "isActive"
> {
  isSystem?: boolean;
  system?: boolean;
  displayName?: string | null;
  category?: RoleCategory;
  isActive?: boolean;
}

function mapRole(dto: RoleDto): Role {
  const isSystem = Boolean(dto.isSystem ?? dto.system);
  return {
    ...dto,
    displayName: dto.displayName ?? null,
    category: dto.category ?? (isSystem ? "SYSTEM" : "BUSINESS"),
    isSystem,
    isActive: dto.isActive ?? true,
  };
}
export interface AccessGroup {
  id: string;
  name: string;
  description: string;
  tenantId: string;
  memberCount: number;
  roles: string[];
}
export interface RbacUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  status: string;
  mfaEnabled: boolean;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  mustChangePassword?: boolean;
  passwordChangedAt?: string | null;
  passwordExpiresAt?: string | null;
  failedLoginCount?: number;
  lockedUntil?: string | null;
  isOwner?: boolean;
}
export interface RbacModule {
  code: string;
  name: string;
  section: string;
  menuRoute: string;
  order: number;
  supportedLevels: RbacLevel[];
  permissionCount?: number;
  description?: string | null;
  levelHelpText?: Partial<Record<RbacLevel, string | null>>;
  internal?: boolean;
  parentModuleCode?: string | null;
}
export interface RoleModuleGrants {
  roleId: string;
  roleName: string;
  grants: ModuleGrant[];
  expandedPermissions: string[];
}
interface AdminUserDto {
  id: string;
  email: string;
  displayName: string | null;
  tenantId: string | null;
  active: boolean;
  mustChangePassword?: boolean;
  mfaEnabled: boolean;
  roles?: string[];
  failedLoginCount?: number;
  lockedUntil?: string | null;
  passwordChangedAt?: string | null;
  passwordExpiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PasswordResetResult {
  userId: string;
  mustChangePassword: boolean;
  passwordChangedAt: string;
  passwordExpiresAt?: string | null;
  revokedSessions: number;
}

export interface UserLookupResult {
  status: "EXISTS_IN_TENANT" | "EXISTS_GLOBAL" | "NOT_FOUND";
  userId: string | null;
  email: string;
  displayName: string | null;
  active: boolean | null;
  membershipStatus: string | null;
}

function mapAdminUser(u: AdminUserDto): RbacUser {
  return {
    id: u.id,
    email: u.email,
    displayName: u.displayName ?? u.email,
    role: u.roles?.[0] ?? "USER",
    status: u.active ? "ACTIVE" : "SUSPENDED",
    mfaEnabled: u.mfaEnabled,
    tenantId: u.tenantId ?? "*",
    mustChangePassword: Boolean(u.mustChangePassword),
    failedLoginCount: u.failedLoginCount ?? 0,
    lockedUntil: u.lockedUntil ?? null,
    passwordChangedAt: u.passwordChangedAt ?? null,
    passwordExpiresAt: u.passwordExpiresAt ?? null,
    createdAt: u.createdAt,
    updatedAt: u.updatedAt,
  };
}

export const rbacApi = {
  users: (search?: string, params: { tenantId?: string } = {}) =>
    call<PagedResponse<AdminUserDto>>(
      `/admin/users${buildQuery({ size: 200, search })}`,
      undefined,
      params.tenantId === "*" ? CROSS_TENANT : undefined,
    ).then((r) =>
      r.content
        .map(mapAdminUser)
        .filter(
          (user) =>
            !params.tenantId ||
            params.tenantId === "*" ||
            user.tenantId === params.tenantId,
        ),
    ),
  createUser: (req: {
    email: string;
    displayName: string;
    role: string;
    tenantId?: string;
    activationMode?: "ACTIVATION_LINK" | "TEMP_PASSWORD";
    password?: string;
    mustChangePassword?: boolean;
    mfaEnabled?: boolean;
  }) => call<AdminUserDto>("/admin/users", jsonBody(req)).then(mapAdminUser),
  /**
   * Verificación email-first (S51 · ADR-0036). Devuelve si el email ya está en el
   * tenant, si existe en el sistema sin afiliar, o si no existe.
   */
  lookupUser: (email: string, tenantId?: string) =>
    call<UserLookupResult>(
      `/admin/users/lookup${buildQuery({ email, tenantId })}`,
    ),
  /** Afilia a un usuario existente al tenant activo (acceso inmediato). */
  affiliateUser: (req: { email: string; role: string; tenantId?: string }) =>
    call<AdminUserDto>("/admin/users/affiliate", jsonBody(req)).then(
      mapAdminUser,
    ),
  /** Reenvía la invitación (enlace o contraseña temporal) a un usuario no activo. */
  resendInvitation: (id: string) =>
    call<{ userId: string; email: string; mode: string }>(
      `/admin/users/${id}/resend-invitation`,
      { method: "POST" },
    ),
  updateUser: (
    id: string,
    req: Partial<
      Pick<RbacUser, "displayName" | "role" | "status" | "mfaEnabled">
    >,
  ) =>
    call<AdminUserDto>(`/admin/users/${id}`, putBody(req)).then(mapAdminUser),
  resetPassword: (
    id: string,
    req: {
      password: string;
      mustChangePassword?: boolean;
      revokeSessions?: boolean;
    },
  ) =>
    call<PasswordResetResult>(
      `/admin/users/${id}/reset-password`,
      jsonBody(req),
    ),
  unlockUser: (id: string) =>
    call<AdminUserDto>(`/admin/users/${id}/unlock`, { method: "POST" }).then(
      mapAdminUser,
    ),
  deleteUser: (id: string) => call<void>(`/admin/users/${id}`, del()),
  modules: () =>
    call<RbacModule[]>("/admin/rbac/modules", undefined, CROSS_TENANT).catch(
      () =>
        RBAC_MODULE_CATALOG.map((module) => ({
          ...module,
          supportedLevels: [...module.supportedLevels],
          permissionCount: 0,
          description: module.name,
          levelHelpText: {},
          internal: false,
          parentModuleCode: null,
        })),
    ),
  roleModuleGrants: (roleIdOrName: string) =>
    call<RoleModuleGrants>(
      `/admin/rbac/roles/${roleIdOrName}/module-grants`,
      undefined,
      CROSS_TENANT,
    ),
  replaceRoleModuleGrants: (roleIdOrName: string, grants: ModuleGrant[]) =>
    call<RoleModuleGrants>(
      `/admin/rbac/roles/${roleIdOrName}/module-grants`,
      putBody({ grants }),
      CROSS_TENANT,
    ),
  permissions: (module?: string) =>
    call<Permission[]>(
      `/admin/rbac/permissions${buildQuery({ module })}`,
      undefined,
      CROSS_TENANT,
    ).catch(() => fallbackPermissions(module)),
  /**
   * Catálogo de roles homologado (S46 Wave A0). Filtrado por categoría en
   * backend: `?category=SYSTEM|BUSINESS`. Sin parámetro devuelve todos.
   */
  roles: (
    params: { category?: RoleCategory; includeInactive?: boolean } = {},
  ) =>
    call<RoleDto[]>(
      `/admin/rbac/roles${buildQuery({
        category: params.category,
        includeInactive: params.includeInactive,
      })}`,
    )
      .then((items) => items.map(mapRole))
      .catch(() =>
        fallbackRoles().filter(
          (role) => !params.category || role.category === params.category,
        ),
      ),
  createRole: (req: {
    name: string;
    description?: string;
    permissions?: string[];
    system?: boolean;
  }) => call<Role>("/admin/rbac/roles", jsonBody(req), CROSS_TENANT),
  updateRole: (
    id: string,
    req: {
      name: string;
      description?: string;
      permissions?: string[];
      system?: boolean;
    },
  ) => call<Role>(`/admin/rbac/roles/${id}`, putBody(req), CROSS_TENANT),
  deleteRole: (id: string) =>
    call<void>(`/admin/rbac/roles/${id}`, del(), CROSS_TENANT),
  replaceRolePermissions: (roleId: string, permissions: string[]) =>
    call<Role>(
      `/admin/rbac/roles/${roleId}/permissions`,
      putBody({ permissions }),
      CROSS_TENANT,
    ),
  grantPermission: (roleId: string, permissionId: string) =>
    call<Role>(
      `/admin/rbac/roles/${roleId}/permissions/${permissionId}`,
      jsonBody({}),
      CROSS_TENANT,
    ),
  revokePermission: (roleId: string, permissionId: string) =>
    call<Role>(
      `/admin/rbac/roles/${roleId}/permissions/${permissionId}`,
      del(),
      CROSS_TENANT,
    ),
  accessGroups: (tenantId?: string) =>
    call<AccessGroup[]>(
      `/admin/rbac/groups${buildQuery({ tenantId })}`,
      undefined,
      CROSS_TENANT,
    ),
  createAccessGroup: (req: {
    name: string;
    description?: string;
    roles?: string[];
    tenantId?: string;
  }) => call<AccessGroup>("/admin/rbac/groups", jsonBody(req), CROSS_TENANT),
  updateAccessGroup: (
    id: string,
    req: {
      name: string;
      description?: string;
      roles?: string[];
      tenantId?: string;
    },
  ) =>
    call<AccessGroup>(`/admin/rbac/groups/${id}`, putBody(req), CROSS_TENANT),
  deleteAccessGroup: (id: string) =>
    call<void>(`/admin/rbac/groups/${id}`, del(), CROSS_TENANT),
};

// ============ Admin · Audit ============
export interface AuditEntry {
  id: string;
  userId?: string | null;
  userEmail?: string | null;
  tenantId?: string | null;
  action: string;
  resource: string;
  resourceId?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  timestamp: string;
}
export interface RbacAuditEntry {
  id: string;
  actorId?: string | null;
  actorEmail?: string | null;
  tenantId?: string | null;
  action: string;
  targetType: string;
  targetId?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  timestamp: string;
}
export interface SystemErrorEntry {
  id: string;
  requestId?: string | null;
  tenantId?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  uri?: string | null;
  method?: string | null;
  exceptionClass?: string | null;
  message?: string | null;
  timestamp: string;
}
interface DateWindowQuery {
  from?: string;
  to?: string;
}
export const auditApi = {
  ops: (page = 0, size = 50, range: DateWindowQuery = {}) =>
    call<PagedResponse<AuditEntry>>(
      `/admin/audit/logs${buildQuery({ page, size, from: range.from, to: range.to })}`,
      undefined,
      CROSS_TENANT,
    ).catch(() => emptyPage<AuditEntry>(page, size)),
  rbac: (page = 0, size = 50, range: DateWindowQuery = {}) =>
    call<PagedResponse<RbacAuditEntry>>(
      `/admin/audit/rbac${buildQuery({ page, size, from: range.from, to: range.to })}`,
      undefined,
      CROSS_TENANT,
    ).catch(() => emptyPage<RbacAuditEntry>(page, size)),
  systemErrors: (page = 0, size = 50, range: DateWindowQuery = {}) =>
    call<PagedResponse<SystemErrorEntry>>(
      `/admin/audit/system-errors${buildQuery({ page, size, from: range.from, to: range.to })}`,
      undefined,
      CROSS_TENANT,
    ).catch(() => emptyPage<SystemErrorEntry>(page, size)),
};

// ============ Admin · Compliance ============
export type ComplianceRequestType =
  | "ACCESS"
  | "ERASURE"
  | "RECTIFICATION"
  | "PORTABILITY"
  | "OBJECTION";
export type ComplianceRequestStatus =
  | "PENDING"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "DENIED";

export interface ComplianceRequest {
  id: string;
  tenantId: string;
  subjectUserId?: string | null;
  subjectEmail?: string | null;
  requestType: ComplianceRequestType;
  status: ComplianceRequestStatus;
  dueDate: string;
  requestedAt: string;
  completedAt?: string | null;
  legalBasis?: string | null;
  notes?: string | null;
  resultExportUrl?: string | null;
  handledBy?: string | null;
}

export interface ComplianceKpis {
  open: number;
  overdue: number;
  completed: number;
}

export interface ComplianceListResponse {
  items: ComplianceRequest[];
  page: number;
  size: number;
  totalElements: number;
  kpis: ComplianceKpis;
}

export interface ComplianceRequestCreatePayload {
  subjectUserId?: string | null;
  subjectEmail?: string | null;
  requestType: ComplianceRequestType;
  legalBasis?: string | null;
  notes?: string | null;
}

export interface ComplianceRequestClosePayload {
  resultExportUrl?: string | null;
  notes?: string | null;
}

export const complianceRequestsApi = {
  list: (
    params: {
      status?: ComplianceRequestStatus;
      page?: number;
      size?: number;
    } = {},
  ) =>
    call<ComplianceListResponse>(
      `/admin/compliance/requests${buildQuery({
        status: params.status,
        page: params.page ?? 0,
        size: params.size ?? 50,
      })}`,
    ),
  create: (payload: ComplianceRequestCreatePayload) =>
    call<ComplianceRequest>("/admin/compliance/requests", jsonBody(payload)),
  start: (id: string) =>
    call<ComplianceRequest>(`/admin/compliance/requests/${id}/start`, {
      method: "POST",
    }),
  complete: (id: string, payload: ComplianceRequestClosePayload) =>
    call<ComplianceRequest>(
      `/admin/compliance/requests/${id}/complete`,
      jsonBody(payload),
    ),
  deny: (id: string, payload: ComplianceRequestClosePayload) =>
    call<ComplianceRequest>(
      `/admin/compliance/requests/${id}/deny`,
      jsonBody(payload),
    ),
  generateAccessExport: (id: string) =>
    call<ComplianceRequest>(
      `/admin/compliance/requests/${id}/generate-access-export`,
      { method: "POST" },
    ),
};

// ============ Admin · Stability ============
export interface CrashEntry {
  id: string;
  tenantId?: string | null;
  userEmail?: string | null;
  fingerprint?: string | null;
  severity: string;
  message?: string | null;
  stackTrace?: string | null;
  resolved: boolean;
  timestamp: string;
}
export interface LatencyEntry {
  id: string;
  tenantId?: string | null;
  source?: string | null;
  category?: string | null;
  operation?: string | null;
  path?: string | null;
  method?: string | null;
  statusCode?: number | null;
  success: boolean;
  durationMs: number;
  requestId?: string | null;
  timestamp: string;
}
export interface UsageEntry {
  id: string;
  tenantId?: string | null;
  userEmail?: string | null;
  sessionId?: string | null;
  country?: string | null;
  deviceType?: string | null;
  browserName?: string | null;
  osName?: string | null;
  path?: string | null;
  timestamp: string;
}
export const stabilityApi = {
  crashes: (days = 30, page = 0, size = 50, range: DateWindowQuery = {}) =>
    call<PagedResponse<CrashEntry>>(
      `/admin/stability/crashes${buildQuery({ days, page, size, from: range.from, to: range.to })}`,
      undefined,
      CROSS_TENANT,
    ).catch(() => emptyPage<CrashEntry>(page, size)),
  latency: (days = 7, page = 0, size = 50, range: DateWindowQuery = {}) =>
    call<PagedResponse<LatencyEntry>>(
      `/admin/stability/latency${buildQuery({ days, page, size, from: range.from, to: range.to })}`,
      undefined,
      CROSS_TENANT,
    ).catch(() => emptyPage<LatencyEntry>(page, size)),
  usage: (days = 7, page = 0, size = 50, range: DateWindowQuery = {}) =>
    call<PagedResponse<UsageEntry>>(
      `/admin/stability/usage${buildQuery({ days, page, size, from: range.from, to: range.to })}`,
      undefined,
      CROSS_TENANT,
    ).catch(() => emptyPage<UsageEntry>(page, size)),
  // collectors
  reportCrash: (payload: {
    fingerprint?: string;
    severity?: string;
    message: string;
    stackTrace?: string;
    metadata?: Record<string, unknown>;
  }) => call<void>("/stability/collect/crash", jsonBody(payload)),
  reportLatency: (payload: {
    category?: string;
    operation: string;
    path?: string;
    method?: string;
    statusCode?: number;
    success?: boolean;
    durationMs: number;
    requestId?: string;
    metadata?: Record<string, unknown>;
  }) => call<void>("/stability/collect/latency", jsonBody(payload)),
  reportUsage: (payload: {
    sessionId?: string;
    country?: string;
    deviceType?: string;
    browserName?: string;
    osName?: string;
    path: string;
    metadata?: Record<string, unknown>;
  }) => call<void>("/stability/collect/usage", jsonBody(payload)),
};

// ============ Admin · System status ============
export type SystemStatusValue = "OK" | "WARN" | "FAIL" | "SKIPPED";

export interface SystemStatusProbe {
  key: string;
  label: string;
  configured: boolean;
  required: boolean;
}

export interface SystemStatusCheck {
  id: string;
  name: string;
  category: string;
  status: SystemStatusValue;
  message: string;
  latencyMs?: number | null;
  target?: string | null;
  items: SystemStatusProbe[];
}

export interface SystemStatusSnapshot {
  checkedAt: string;
  overallStatus: SystemStatusValue;
  checks: SystemStatusCheck[];
}

export const systemStatusApi = {
  current: () =>
    call<SystemStatusSnapshot>("/admin/system/status", undefined, CROSS_TENANT),
};

// ============ Admin · System config ============
export interface SystemConfigEntry {
  key: string;
  value: string | null;
  valueType: string;
  description?: string | null;
  sensitive: boolean;
  enumValues?: string[] | null;
  scope?: string | null;
  tenantId?: string | null;
  category?: string | null;
  editableBy?: string | null;
  publicVisible?: boolean;
  updatedAt?: string | null;
}
interface SystemConfigDto {
  key: string;
  value: unknown;
  valueType: string;
  enumValues?: unknown;
  scope?: string | null;
  tenantId?: string | null;
  description?: string | null;
  category?: string | null;
  editableBy?: string | null;
  publicVisible?: boolean;
  updatedAt?: string | null;
  sensitive?: boolean;
}

function normalizeEnumValues(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.map((item) => String(item));
}

function mapSystemConfig(c: SystemConfigDto): SystemConfigEntry {
  return {
    key: c.key,
    value: stringifyConfigValue(c.value),
    valueType: c.valueType,
    enumValues: normalizeEnumValues(c.enumValues),
    scope: c.scope,
    tenantId: c.tenantId,
    description: c.description,
    category: c.category,
    editableBy: c.editableBy,
    publicVisible: c.publicVisible,
    updatedAt: c.updatedAt,
    sensitive: Boolean(c.sensitive),
  };
}

export const systemConfigApi = {
  list: () =>
    call<SystemConfigDto[]>(
      "/admin/system/config",
      undefined,
      CROSS_TENANT,
    ).then((items) => items.map(mapSystemConfig)),
  get: (key: string) =>
    call<SystemConfigDto>(
      `/admin/system/config/${encodeURIComponent(key)}`,
      undefined,
      CROSS_TENANT,
    ).then(mapSystemConfig),
  documentMaxUploadMb: () =>
    systemConfigApi
      .get("document.max_upload_mb")
      .then((item) => Number(item.value ?? 50))
      .then((value) => (Number.isFinite(value) && value > 0 ? value : 50))
      .catch(() => 50),
  set: (key: string, value: string, valueType = "STRING") =>
    call<SystemConfigDto>(
      `/admin/system/config/${encodeURIComponent(key)}`,
      putBody({ value: parseConfigValue(value, valueType) }),
      CROSS_TENANT,
    ).then(mapSystemConfig),
  tenantList: (tenantId: string) =>
    call<SystemConfigDto[]>(`/tenants/${tenantId}/config`, undefined, {
      tenantIdOverride: tenantId,
    }).then((items) => items.map(mapSystemConfig)),
  tenantSet: (
    tenantId: string,
    key: string,
    value: string,
    valueType = "STRING",
  ) =>
    call<SystemConfigDto>(
      `/tenants/${tenantId}/config/${encodeURIComponent(key)}`,
      putBody({ value: parseConfigValue(value, valueType) }),
      { tenantIdOverride: tenantId },
    ).then(mapSystemConfig),
};

// ============ Tenant settings ============
export interface TenantSettings {
  id: string;
  tenantId: string;
  mfaRequired: boolean;
  sessionTimeoutMinutes: number;
  ipAllowlist?: string | null;
  retentionDays: number;
  ssoProvider?: string | null;
  ssoConfig: Record<string, unknown>;
}
export const tenantSettingsApi = {
  get: (tenantId: string) =>
    call<TenantSettings>(`/tenants/${tenantId}/settings`),
  update: (
    tenantId: string,
    body: Partial<Omit<TenantSettings, "id" | "tenantId">>,
  ) => call<TenantSettings>(`/tenants/${tenantId}/settings`, putBody(body)),
};

// ============ Tasks (inbox) — BLOCKER-TK01 ============
export type TaskPriority = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
export type TaskStatus =
  | "PENDING"
  | "IN_PROGRESS"
  | "IN_REVIEW"
  | "DONE"
  | "CANCELLED";
export interface AppTask {
  id: string;
  title: string;
  projectId?: string | null;
  projectName?: string | null;
  dueAt?: string | null;
  priority: TaskPriority;
  sla?: string | null;
  status: TaskStatus;
  assigneeId?: string | null;
  assigneeName?: string | null;
}
export interface TaskMetrics {
  pending: number;
  dueToday: number;
  inProgress: number;
  closedWeek: number;
}
export const tasksApi = {
  list: (
    scope: "mine" | "team" | "all" | "overdue" = "mine",
    page = 0,
    size = 50,
  ) =>
    call<PagedResponse<AppTask>>(
      `/tasks?scope=${scope}&page=${page}&size=${size}`,
    ),
  metrics: () => call<TaskMetrics>(`/tasks/metrics`),
  update: (
    id: string,
    body: Partial<Pick<AppTask, "status" | "priority" | "assigneeId">>,
  ) => call<AppTask>(`/tasks/${id}`, putBody(body)),
};

// ============ Dashboard metrics — BLOCKER-D01 ============
export interface DashboardKpi {
  activeDocs: number;
  myTasks: {
    total: number;
    dueToday: number;
    high: number;
    medium: number;
    low: number;
  };
  avgReviewMinutes: number;
  openRisks: number;
  /** Tokens IA (prompt + completion) consumidos por el usuario en el mes. */
  aiTokensMonth: number;
  trends: {
    docsPerDay: number[];
    aiCalls: number[];
    /** Serie de 7 días de tokens IA del usuario. */
    aiTokens: number[];
    reviewMinutes: number[];
    risksOpen: number[];
  };
}
// ============ Dashboard ejecutivo — GET /reports/dashboard (S43) ============
export type DashboardPriority = "HIGH" | "MEDIUM" | "LOW";

export interface DashboardMyWorkItem {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  priority: DashboardPriority;
  status: string;
  dueDate: string | null;
  overdue: boolean;
}

export interface DashboardAiModelUsage {
  provider: string;
  model: string;
  calls: number;
  tokens: number;
}

export interface DashboardAiActivity {
  callsLast7d: number;
  tokensLast30d: number;
  topModels: DashboardAiModelUsage[];
}

export interface DashboardTemplateUsage {
  id: string;
  name: string;
  rendersLast30d: number;
  lastRenderedAt: string | null;
}

export interface DashboardWorkflowStatus {
  definitionId: string;
  code: string;
  name: string;
  runningCount: number;
  slaBreachedCount: number;
  oldestStartedAt: string | null;
}

export interface DashboardRagSlice {
  label: string;
  value: number;
}

export interface DashboardRagCoverage {
  totalDocs: number;
  syncedDocs: number;
  coveragePct: number;
  slices: DashboardRagSlice[];
}

export interface DashboardPinnedMatter {
  id: string;
  name: string;
  status: string;
  riskScore: number;
  openTasks: number;
  docsCount: number;
  pinnedAt: string | null;
}

export interface DashboardRecentDocument {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
  status: string;
  updatedAt: string;
  mine: boolean;
}

export interface DashboardSummary {
  kpis: DashboardKpi;
  myWork: DashboardMyWorkItem[];
  aiActivity: DashboardAiActivity;
  templates: DashboardTemplateUsage[];
  workflows: DashboardWorkflowStatus[];
  ragCoverage: DashboardRagCoverage;
  pinnedMatters: DashboardPinnedMatter[];
  recentDocuments: DashboardRecentDocument[];
}

export const dashboardApi = {
  /** Legacy: KPIs básicos. Se mantiene como fallback degradado. */
  kpis: () => call<DashboardKpi>(`/dashboard/kpis`),
  /** Dashboard ejecutivo consolidado (permiso `report.dashboard.view`). */
  summary: () => call<DashboardSummary>(`/reports/dashboard`),
};

// ============ Templates + clauses + playbooks — BLOCKER-T01 ============
export type TemplateScope = "GLOBAL" | "TENANT" | "PROJECT";
export interface TemplateItem {
  id: string;
  code: string;
  name: string;
  type: string;
  scope: TemplateScope;
  jurisdiction: string;
  uses: number;
  variables: number;
  updatedAt: string;
}
export interface ClauseItem {
  id: string;
  code: string;
  name: string;
  category: string;
  jurisdiction: string;
  scope: TemplateScope;
  risk: "BAJO" | "MEDIO" | "ALTO";
  uses: number;
}
export interface PlaybookItem {
  id: string;
  code?: string;
  name: string;
  positions: {
    ideal?: string;
    acceptable?: string;
    minimum?: string;
    nonNegotiable?: string;
  };
}
export interface GovernanceApproval {
  id: string;
  resourceType: "CLAUSE" | "PLAYBOOK" | "TEMPLATE";
  resourceCode: string;
  title: string;
  diffSummary?: string;
  submittedBy: string;
  submittedAt: string;
}
export const templatesApi = {
  list: (scope?: TemplateScope, q?: string) =>
    call<PagedResponse<TemplateItem>>(
      `/templates?${scope ? `scope=${scope}&` : ""}${q ? `q=${encodeURIComponent(q)}` : ""}`,
    ),
  clauses: (filters?: {
    category?: string;
    jurisdiction?: string;
    scope?: TemplateScope;
    q?: string;
  }) => {
    const p = new URLSearchParams();
    if (filters?.category) p.set("category", filters.category);
    if (filters?.jurisdiction) p.set("jurisdiction", filters.jurisdiction);
    if (filters?.scope) p.set("scope", filters.scope);
    if (filters?.q) p.set("q", filters.q);
    return call<PagedResponse<ClauseItem>>(`/clauses?${p.toString()}`);
  },
  playbooks: () => call<PlaybookItem[]>(`/playbooks`),
  approvals: () => call<GovernanceApproval[]>(`/templates/approvals/pending`),
};

export interface PlaybookComparisonResult {
  id: string;
  documentId: string;
  documentVersion: number;
  playbookId: string;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED";
  overallScore?: number | null;
  clausesAnalyzed?: number;
  clausesIdeal?: number;
  clausesAcceptable?: number;
  clausesMinimum?: number;
  clausesNonNegotiable?: number;
  clausesMissing?: number;
  resultJsonb?: {
    clauseResults?: Array<{
      clauseType?: string;
      playbookTitle?: string;
      foundInDocument?: boolean;
      matchedLevel?: string;
      confidence?: number;
      documentExcerpt?: string;
      anchorParagraphId?: string | null;
      gapAnalysis?: string;
      recommendation?: string;
    }>;
  };
  modelUsed?: string | null;
  costUsd?: number | null;
  createdAt?: string;
}

export interface PlaybookMitigationResponse {
  mitigations: Array<{
    strategy: string;
    proposed_text?: string;
    proposedText?: string;
    rationale?: string;
    risk_residual?: string;
    riskResidual?: string;
    negotiation_arguments?: string[];
    negotiationArguments?: string[];
    fallback_position?: string;
    fallbackPosition?: string;
  }>;
  output?: unknown;
  modelUsed?: string | null;
  latencyMs?: number | null;
}

export const playbookCompareApi = {
  start: (documentId: string, playbookId: string) =>
    call<{ id: string; status: string; overallScore?: number }>(
      `/documents/${documentId}/playbook-compare`,
      jsonBody({ playbookId }),
    ),
  list: (documentId: string) =>
    call<PlaybookComparisonResult[]>(
      `/documents/${documentId}/playbook-comparisons`,
    ),
  get: (comparisonId: string) =>
    call<PlaybookComparisonResult>(`/playbook-comparisons/${comparisonId}`),
  mitigation: (documentId: string, comparisonId: string, clauseType: string) =>
    call<PlaybookMitigationResponse>(
      `/documents/${documentId}/playbook-comparisons/${comparisonId}/clauses/${encodeURIComponent(clauseType)}/mitigation`,
      jsonBody({}),
    ),
};

// ============ AI Assistant — BLOCKER-A01..A05 ============
// NO ELIMINAR — fix P14B · cliente legacy del asistente IA muerto eliminado.
// Las tabs Modelos/Prompts/Guardrails/Métricas del AIAssistantPage fueron
// eliminadas en el ciclo del prompt 05; el objeto y sus types quedaron como
// código muerto y se eliminaron acá. NO REINTRODUCIR.
// Ver prompts/agents/14B-codex-limpieza-mocks-residuales.md

// ============ Risks — BLOCKER-RK01 + Sprint 22C-1 ============
export type RiskSeverity = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
export interface RiskPattern {
  id: string;
  tenantId?: string;
  code: string;
  name: string;
  description?: string | null;
  category: string;
  severity: RiskSeverity | "Bajo" | "Medio" | "Alto" | "Crítico";
  detectionRegex?: string | null;
  detectionHint?: string | null;
  enabled?: boolean;
  hits?: number;
  trend?: number[];
  createdAt?: string;
  updatedAt?: string;
}
export interface RiskFinding {
  id: string;
  tenantId?: string;
  patternId?: string | null;
  patternCode?: string;
  riskCategoryId?: string | null;
  riskCategoryCode?: string | null;
  riskCategoryName?: string | null;
  riskCategoryColorHex?: string | null;
  category: string;
  projectId: string;
  projectName?: string;
  documentId?: string;
  severity: RiskSeverity;
  detectedAt: string;
  createdAt: string;
  updatedAt?: string;
  score: number;
  severityScore: number;
  likelihoodScore: number;
  compositeScore?: number | null;
  title: string;
  description?: string | null;
  citation?: string | null;
  status:
    | "OPEN"
    | "ACKNOWLEDGED"
    | "MITIGATED"
    | "ACCEPTED"
    | "FALSE_POSITIVE"
    | "RESOLVED";
}
export interface RiskThresholds {
  green: number;
  saffron: number;
  gold: number;
  red: number;
}
export interface RiskCategory {
  id: string;
  tenantId?: string | null;
  code: string;
  name: string;
  description?: string | null;
  colorHex: string;
  defaultWeight: number;
  system: boolean;
  active: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}
export interface RiskCategoryPayload {
  code: string;
  name: string;
  description?: string | null;
  colorHex: string;
  defaultWeight: number;
  active: boolean;
  sortOrder: number;
}
export interface ScoringPolicy {
  id: string;
  tenantId: string;
  name: string;
  formula: "WEIGHTED_AVG" | "MAX" | "CUSTOM_SQL";
  weights: Record<string, number>;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}
export interface ScoringPolicyPayload {
  name: string;
  formula: "WEIGHTED_AVG" | "MAX" | "CUSTOM_SQL";
  weights: Record<string, number>;
  active: boolean;
}
export interface RiskCompositeBreakdown {
  categoryId?: string | null;
  categoryCode: string;
  categoryName: string;
  colorHex: string;
  openFindings: number;
  weight: number;
  avgCompositeScore: number;
  maxCompositeScore: number;
  weightedScore: number;
}
export interface RiskComposite {
  projectId: string;
  overallScore: number;
  formula: "WEIGHTED_AVG" | "MAX" | "CUSTOM_SQL";
  activePolicyId?: string | null;
  breakdown: RiskCompositeBreakdown[];
}
export interface RiskHeatmapCell {
  projectId: string | null;
  projectName: string | null;
  category: string;
  openFindings: number;
  avgScore: number;
  maxScore: number;
  criticalCount: number;
}

function normalizeRiskPattern(pattern: RiskPattern): RiskPattern {
  return {
    ...pattern,
    hits: pattern.hits ?? 0,
    trend: pattern.trend ?? [],
  };
}

function normalizeRiskFinding(finding: RiskFinding): RiskFinding {
  return {
    ...finding,
    patternCode:
      finding.patternCode ?? finding.patternId?.slice(0, 8) ?? "manual",
    projectName: finding.projectName ?? finding.projectId,
    detectedAt: finding.detectedAt ?? finding.createdAt,
  };
}

export const risksApi = {
  patterns: async () => {
    const page = await call<PagedResponse<RiskPattern>>(
      `/risks/patterns?size=200`,
    );
    return page.content.map(normalizeRiskPattern);
  },
  findings: async (filters?: {
    status?: string;
    minScore?: number;
    severity?: string;
    category?: string;
    projectId?: string;
  }) => {
    const p = new URLSearchParams();
    if (filters?.status) p.set("status", filters.status);
    if (filters?.minScore) p.set("minScore", String(filters.minScore));
    if (filters?.severity) p.set("severity", filters.severity);
    if (filters?.category) p.set("category", filters.category);
    if (filters?.projectId) p.set("projectId", filters.projectId);
    const page = await call<PagedResponse<RiskFinding>>(
      `/risks/findings?${p.toString()}`,
    );
    return { ...page, content: page.content.map(normalizeRiskFinding) };
  },
  thresholds: () => call<RiskThresholds>(`/risks/thresholds`),
  updateThresholds: (body: RiskThresholds) =>
    call<RiskThresholds>(`/risks/thresholds`, putBody(body)),
  heatmap: () =>
    call<{ rows: string[]; cols: string[]; values: number[][] }>(
      `/risks/heatmap`,
    ),
  /** Heatmap v2 (risk-service real): array de celdas con projectId, category, openFindings, avgScore. */
  heatmapV2: (filters?: {
    projectId?: string;
    category?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => call<RiskHeatmapCell[]>(`/risks/heatmap${buildQuery(filters ?? {})}`),
  riskCategories: (includeInactive = false) =>
    call<RiskCategory[]>(`/risk-categories${buildQuery({ includeInactive })}`),
  createRiskCategory: (body: RiskCategoryPayload) =>
    call<RiskCategory>(`/risk-categories`, jsonBody(body)),
  updateRiskCategory: (id: string, body: RiskCategoryPayload) =>
    call<RiskCategory>(`/risk-categories/${id}`, putBody(body)),
  deleteRiskCategory: (id: string) =>
    call<RiskCategory>(`/risk-categories/${id}`, del()),
  scoringPolicies: () => call<ScoringPolicy[]>(`/scoring-policies`),
  createScoringPolicy: (body: ScoringPolicyPayload) =>
    call<ScoringPolicy>(`/scoring-policies`, jsonBody(body)),
  updateScoringPolicy: (id: string, body: ScoringPolicyPayload) =>
    call<ScoringPolicy>(`/scoring-policies/${id}`, putBody(body)),
  deleteScoringPolicy: (id: string) =>
    call<void>(`/scoring-policies/${id}`, del()),
  riskComposite: (projectId: string) =>
    call<RiskComposite>(`/projects/${projectId}/risk-composite`),
  /**
   * Crea un hallazgo de riesgo manual (promueve una alerta IA a la matriz de
   * riesgos). Requiere permiso `risk.scoring.admin` en el backend.
   */
  createFinding: (body: {
    projectId: string;
    documentId?: string;
    categoryCode: string;
    severity: string;
    severityScore: number;
    title: string;
    description?: string;
    citation?: string;
  }) =>
    call<RiskFinding>(`/risks/findings`, jsonBody(body)).then(
      normalizeRiskFinding,
    ),
};

// ============ Reports — BLOCKER-RP01 ============
export interface PortfolioBucket {
  industry: string;
  count: number;
  amountUsd: number;
}
export interface CounterpartyEntry {
  name: string;
  agreements: number;
  lastAt: string;
}
export interface ObligationEntry {
  id: string;
  documentId: string;
  documentName: string;
  dueAt: string;
  type: string;
  project: string;
}
export interface ProductivityMetric {
  label: string;
  value: number;
  delta?: number;
}
export interface AiCostBucket {
  provider: string;
  model: string;
  usd: number;
  calls: number;
}
export interface ReportWidgetMetric {
  field: string;
  agg: "count" | "sum" | "avg" | "min" | "max";
}
export interface ReportWidgetConfig {
  id: string;
  type:
    | "kpi"
    | "bar"
    | "line"
    | "pie"
    | "donut"
    | "table"
    | "heatmap"
    | "funnel";
  datasource: string;
  metric: ReportWidgetMetric;
  group_by?: string;
  title: string;
}
export interface ReportConfig {
  version: string;
  stakeholder?: string;
  widgets: ReportWidgetConfig[];
}
export interface ReportDefinition {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  category: "executive" | "operational" | "compliance" | "financial" | "custom";
  config: ReportConfig;
  system: boolean;
  active: boolean;
  ownerUserId?: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface ReportWidgetResult {
  id: string;
  title: string;
  type: string;
  data: Array<Record<string, string | number | boolean | null>>;
}
export interface ReportExecutionResult {
  executionId?: string;
  durationMs?: number;
  definition?: ReportDefinition;
  widgets: ReportWidgetResult[];
  executedAt?: string;
  previewedAt?: string;
}
export const reportsApi = {
  portfolio: () =>
    call<{
      buckets: PortfolioBucket[];
      topCounterparties: CounterpartyEntry[];
    }>(`/reports/portfolio`),
  obligations: () => call<ObligationEntry[]>(`/reports/obligations`),
  exec: (period: "MONTH" | "QUARTER" | "YEAR" = "MONTH") =>
    call<{ html: string; period: string }>(`/reports/exec?period=${period}`),
  productivity: () => call<ProductivityMetric[]>(`/reports/productivity`),
  aiCost: (period = "MONTH") =>
    call<AiCostBucket[]>(`/reports/ai-cost?period=${period}`),
  definitions: (includeInactive = false) =>
    call<ReportDefinition[]>(
      `/reports/definitions?includeInactive=${includeInactive}`,
    ),
  dashboards: () => call<ReportDefinition[]>(`/reports/dashboards`),
  createDefinition: (body: {
    code: string;
    name: string;
    description?: string;
    category: ReportDefinition["category"];
    config: ReportConfig;
  }) => call<ReportDefinition>(`/reports/definitions`, jsonBody(body)),
  executeDefinition: (id: string, filters: Record<string, unknown> = {}) =>
    call<ReportExecutionResult>(
      `/reports/definitions/${id}/execute`,
      jsonBody(filters),
    ),
  preview: (config: ReportConfig) =>
    call<ReportExecutionResult>(`/reports/preview`, jsonBody({ config })),
};

export interface CostCenterSummary {
  total: number;
  invocations: number;
  tokens_in?: number;
  tokens_out?: number;
  currency: "USD" | "PEN";
  from: string;
  to: string;
  forecast?: CostForecast;
}
export interface CostForecast {
  currentUsd: number;
  projectedUsd: number;
  varianceUsd: number;
  method: string;
}
export interface CostDrilldownRow {
  bucket: string;
  total: number;
  invocations: number;
  tokens_in?: number;
  tokens_out?: number;
}
export interface CostBudget {
  id: string;
  scope_type?: string;
  scopeType?: string;
  scope_id?: string;
  scopeId?: string;
  period: string;
  budget_usd?: number;
  budgetUsd?: number;
  warn_pct?: number;
  warnPct?: number;
  throttle_pct?: number;
  throttlePct?: number;
  hard_stop_pct?: number;
  hardStopPct?: number;
  fallback_profile?: string;
  fallbackProfile?: string;
  is_active?: boolean;
  active?: boolean;
  current_spent_usd?: number;
  currentSpentUsd?: number;
  current_period_start?: string;
  currentPeriodStart?: string;
  current_period_end?: string;
  currentPeriodEnd?: string;
}
export interface CostAnomaly {
  id: string;
  detected_at?: string;
  detectedAt?: string;
  scope_type?: string;
  scopeType?: string;
  expected_usd?: number;
  expectedUsd?: number;
  actual_usd?: number;
  actualUsd?: number;
  sigmas?: number;
  top_drivers_jsonb?: Array<Record<string, unknown>>;
  topDrivers?: Array<Record<string, unknown>>;
  status: string;
  resolution_notes?: string | null;
  resolutionNotes?: string | null;
}
export interface CostRecommendation {
  skill: string;
  current_model?: string;
  currentModel?: string;
  recommendedModel: string;
  estimatedSavingsUsdMonthly: number;
  qualityImpact: "LOW" | "MEDIUM" | "HIGH";
  confidence: number;
  rationale: string;
}
export interface CostRecommendations {
  potentialSavingsUsd: number;
  recommendations: CostRecommendation[];
}
export interface CostChargebackRow {
  counterparty_id?: string;
  counterpartyId?: string;
  invocations: number;
  total_cost_usd?: number;
  totalCostUsd?: number;
  total_cost_pen?: number;
  totalCostPen?: number;
}
export interface ClientInvoice {
  id: string;
  counterparty_id?: string;
  counterpartyId?: string;
  total_cost_usd?: number;
  totalCostUsd?: number;
  total_billable_usd?: number;
  totalBillableUsd?: number;
  total_billable_pen?: number;
  totalBillablePen?: number;
  invocations_count?: number;
  invocationsCount?: number;
  status: string;
}
export interface ExchangeRate {
  baseCurrency: string;
  quoteCurrency: string;
  rate: number;
  source: string;
  effectiveDate?: string;
}
export const costCenterApi = {
  summary: (params: URLSearchParams) =>
    call<CostCenterSummary>(`/admin/cost/summary?${params.toString()}`),
  drilldown: (params: URLSearchParams) =>
    call<CostDrilldownRow[]>(`/admin/cost/drilldown?${params.toString()}`),
  forecast: (params: URLSearchParams) =>
    call<CostForecast>(`/admin/cost/forecast?${params.toString()}`),
  budgets: () => call<CostBudget[]>(`/admin/cost/budgets`),
  upsertBudget: (body: {
    scopeType: "TENANT" | "PROJECT" | "USER";
    scopeId?: string;
    period: "MONTHLY" | "WEEKLY" | "DAILY";
    budgetUsd: number;
    warnPct: number;
    throttlePct: number;
    hardStopPct: number;
    fallbackProfile: string;
  }) => call<CostBudget>(`/admin/cost/budgets`, putBody(body)),
  anomalies: (status?: string) =>
    call<CostAnomaly[]>(
      `/admin/cost/anomalies${status ? `?status=${encodeURIComponent(status)}` : ""}`,
    ),
  evaluateAnomalies: () =>
    call<Record<string, unknown>>(
      `/admin/cost/anomalies/evaluate`,
      jsonBody({}),
    ),
  updateAnomaly: (
    id: string,
    body: { status: string; resolutionNotes?: string },
  ) => call<CostAnomaly>(`/admin/cost/anomalies/${id}`, putBody(body)),
  recommendations: () =>
    call<CostRecommendations>(`/admin/cost/recommendations`),
  chargeback: (params: URLSearchParams) =>
    call<CostChargebackRow[]>(`/admin/cost/chargeback?${params.toString()}`),
  createInvoice: (body: {
    counterpartyId: string;
    periodStart?: string;
    periodEnd?: string;
    markupPct?: number;
  }) => call<ClientInvoice>(`/admin/cost/chargeback/invoices`, jsonBody(body)),
  latestExchangeRate: () =>
    call<ExchangeRate>(`/admin/cost/exchange-rates/latest`),
};

// ============ Document content (editor) helpers — extensions ============
export interface DocAstNode {
  id: string;
  level: number;
  title: string;
  risk: number;
  children?: DocAstNode[];
}
export interface DocCommentEntry {
  id: string;
  documentId?: string;
  parentId?: string | null;
  versionNumber?: number | null;
  anchorPath?: string | null;
  selectionStart?: number | null;
  selectionEnd?: number | null;
  anchorFrom?: number | null;
  anchorTo?: number | null;
  author?: string;
  authorEmail?: string | null;
  createdAt: string;
  updatedAt?: string;
  body: string;
  resolved: boolean;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  status?: "OPEN" | "RESOLVED" | "DELETED";
  section?: string;
}
export interface DocRiskFinding {
  id: string;
  code?: string;
  title?: string;
  category?: string;
  riskCategoryCode?: string | null;
  riskCategoryName?: string | null;
  severity: string;
  severityScore?: number;
  likelihoodScore?: number;
  compositeScore?: number | null;
  score?: number;
  description: string;
  section?: string;
  citation?: string | null;
  notes?: string | null;
  status?: string;
}

export type DocumentAiRequestStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

/** Tipo de hallazgo devuelto por el análisis IA del documento completo. */
export type DocumentAnalysisFindingType =
  | "RISK"
  | "CRITICAL_CLAUSE"
  | "RECOMMENDATION";

export interface DocumentAnalysisFinding {
  type: DocumentAnalysisFindingType;
  text: string;
}

export interface DocumentAnalysisCitation {
  position?: number | null;
  sourceTitle?: string | null;
  sourceUrl?: string | null;
  excerpt?: string | null;
  relevanceScore?: number | null;
  chunkId?: string | null;
  sourceId?: string | null;
}

/**
 * Resultado persistido en `result_jsonb` para corridas de análisis
 * (`requestType = analyze_document`). `score` va de 0 a 100;
 * mayor = más riesgo.
 */
export interface DocumentAnalysisResult {
  summary?: string;
  findings?: DocumentAnalysisFinding[];
  citations?: DocumentAnalysisCitation[];
  score?: number;
}

/**
 * Envoltorio que el Orchestrator devuelve para skills genéricas
 * (auto-ingest): `parsed` es el JSON estructurado del skill
 * (p. ej. `{ violaciones: [...] }`), `raw` el texto crudo del modelo.
 */
export interface SkillOutputEnvelope {
  raw?: string;
  parsed?: Record<string, unknown> | null;
  parse_error?: boolean;
}

export interface DocumentAiRequestResult extends DocumentAnalysisResult {
  suggestedText?: string;
  reviewerApplied?: boolean;
  appliedToDocument?: boolean;
  appliedAt?: string;
  appliedBy?: string;
  primaryModel?: string;
  latencyMs?: number;
  /** Mensaje legible de skills genéricas (texto crudo del modelo). */
  message?: string;
  /** Salida estructurada de skills genéricas auto-ingest. */
  output?: SkillOutputEnvelope | null;
}

export interface DocumentAiRequestEntry {
  id: string;
  documentId: string;
  userId?: string | null;
  requestType: string;
  prompt: string;
  selectedText?: string | null;
  selectedFrom?: number | null;
  selectedTo?: number | null;
  tone?: string | null;
  status: DocumentAiRequestStatus;
  /** MANUAL (pedido del usuario) o AUTO (análisis disparado en la ingesta). */
  triggeredBy?: string | null;
  resultJsonb?: DocumentAiRequestResult | null;
  errorMessage?: string | null;
  modelUsed?: string | null;
  costUsd?: number | null;
  scopeContext?: Record<string, unknown> | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
}

export interface CreateDocumentAiRequestBody {
  requestType: string;
  prompt: string;
  selectedText?: string;
  selectedFrom?: number;
  selectedTo?: number;
  tone?: string;
  scopeContext?: Record<string, unknown>;
}

export interface DocumentCompletionItem {
  text: string;
  confidence: number;
  rationale?: string | null;
  playbook_clause_ref?: string | null;
}

export interface DocumentCompletionResponse {
  eventId: string;
  completions: DocumentCompletionItem[];
  output?: unknown;
  modelUsed?: string | null;
  latencyMs?: number | null;
  costUsd?: number | null;
}

export interface DocumentJurisprudenciaPrecedent {
  id: string;
  instancia: string;
  fecha?: string | null;
  sumilla: string;
  relevancia_score?: number;
  relevanciaScore?: number;
  relacion?: "FAVORABLE" | "ADVERSO" | "MIXTO" | "NEUTRAL";
  argumento?: string;
  url_externa?: string | null;
  urlExterna?: string | null;
}

export interface DocumentJurisprudenciaResponse {
  summary: string;
  precedents: DocumentJurisprudenciaPrecedent[];
  recommendation: string;
  output?: unknown;
  modelUsed?: string | null;
  latencyMs?: number | null;
}

export interface DocumentSemanticClauseChange {
  clauseType: string;
  changeType: "ADDED" | "REMOVED" | "MODIFIED";
  oldText?: string | null;
  newText?: string | null;
  severity: "LOW" | "MEDIUM" | "HIGH";
}

export interface DocumentSemanticAmountChange {
  label: string;
  oldAmount: number;
  newAmount: number;
  currency: string;
}

export interface DocumentSemanticDiffResponse {
  summary: string;
  clauseChanges: DocumentSemanticClauseChange[];
  amountChanges: DocumentSemanticAmountChange[];
  fromVersion: number;
  toVersion: number;
}

export const documentExtApi = {
  ast: (docId: string) => call<DocAstNode[]>(`/documents/${docId}/ast`),
  comments: async (docId: string) => {
    const page = await call<PagedResponse<DocCommentEntry>>(
      `/documents/${docId}/comments`,
    );
    return page.items ?? page.content;
  },
  getComment: (docId: string, commentId: string) =>
    call<DocCommentEntry>(`/documents/${docId}/comments/${commentId}`),
  addComment: (
    docId: string,
    body: {
      section?: string;
      body?: string;
      anchorPath?: string;
      anchorFrom?: number;
      anchorTo?: number;
      text?: string;
      parentId?: string;
    },
  ) => call<DocCommentEntry>(`/documents/${docId}/comments`, jsonBody(body)),
  resolveComment: (docId: string, commentId: string) =>
    call<DocCommentEntry>(
      `/documents/${docId}/comments/${commentId}/resolve`,
      jsonBody({}),
    ),
  reopenComment: (docId: string, commentId: string) =>
    call<DocCommentEntry>(
      `/documents/${docId}/comments/${commentId}/reopen`,
      jsonBody({}),
    ),
  deleteComment: (docId: string, commentId: string) =>
    call<DocCommentEntry>(`/documents/${docId}/comments/${commentId}`, del()),
  risks: (docId: string) =>
    call<DocRiskFinding[]>(`/documents/${docId}/risks`).catch(
      (error: unknown) => {
        if (error instanceof BackendError && error.status === 404) {
          return [];
        }
        throw error;
      },
    ),
  aiRequests: (
    docId: string,
    params: { status?: string; limit?: number; type?: string } = {},
  ) =>
    call<DocumentAiRequestEntry[]>(
      `/documents/${docId}/ai-requests${buildQuery({ status: params.status, type: params.type, limit: params.limit ?? 20 })}`,
    ),
  createAiRequest: (docId: string, body: CreateDocumentAiRequestBody) =>
    call<DocumentAiRequestEntry>(
      `/documents/${docId}/ai-requests`,
      jsonBody(body),
    ),
  getAiRequest: (docId: string, requestId: string) =>
    call<DocumentAiRequestEntry>(
      `/documents/${docId}/ai-requests/${requestId}`,
    ),
  cancelAiRequest: (docId: string, requestId: string) =>
    call<DocumentAiRequestEntry>(
      `/documents/${docId}/ai-requests/${requestId}/cancel`,
      jsonBody({}),
    ),
  applyAiRequest: (docId: string, requestId: string) =>
    call<DocumentAiRequestEntry>(
      `/documents/${docId}/ai-requests/${requestId}/apply`,
      jsonBody({}),
    ),
  completions: (
    docId: string,
    body: {
      cursorPosition: number;
      contextBefore: string;
      contextAfter?: string;
      maxCompletions?: number;
    },
  ) =>
    call<DocumentCompletionResponse>(
      `/documents/${docId}/completions`,
      jsonBody({
        cursorPosition: body.cursorPosition,
        contextBefore: body.contextBefore,
        contextAfter: body.contextAfter,
        maxCompletions: body.maxCompletions,
      }),
    ),
  trackCompletion: (
    docId: string,
    eventId: string,
    body: { acceptedIndex: number; acceptedText?: string },
  ) =>
    call<{
      eventId: string;
      acceptedIndex: number;
      acceptedText?: string;
      trackedAt: string;
    }>(`/documents/${docId}/completions/${eventId}`, patchBody(body)),
  jurisprudencia: (
    docId: string,
    body: {
      clauseExcerpt?: string;
      matterType?: string;
      filters?: {
        instancia?: "INDECOPI" | "MEF" | "PJ" | "TC";
        materia?: string;
        fechaDesde?: string;
        fechaHasta?: string;
      };
      maxPrecedents?: number;
    },
  ) =>
    call<DocumentJurisprudenciaResponse>(
      `/documents/${docId}/jurisprudencia`,
      jsonBody(body),
    ),
  semanticDiff: (docId: string, fromVersion: number, toVersion: number) =>
    call<DocumentSemanticDiffResponse>(
      `/documents/${docId}/diff/semantic?from=${fromVersion}&to=${toVersion}`,
    ),
};

// ============ Notifications — notification-service /notifications/* (unifica BLOCKER-N01) ============
export interface NotificationItem {
  id: string;
  // Campos canónicos (notification-service real)
  tenantId?: string | null;
  category?: string;
  title: string;
  body: string;
  linkUrl?: string | null;
  severity?: "INFO" | "WARN" | "ERROR" | "SUCCESS";
  read: boolean;
  readAt?: string | null;
  createdAt: string;
  // Campos retrocompatibles (Topbar legacy)
  icon?: string;
  tone?: "neutral" | "info" | "warn" | "danger" | "brand";
}

/**
 * Cliente unificado para notification-service. El endpoint /notifications
 * devuelve PagedResponse en producción y un array plano en la demo del
 * gateway. El cliente acepta ambos y normaliza a {@link NotificationItem}[].
 */
async function fetchNotificationsList(
  page = 0,
  size = 20,
): Promise<NotificationItem[]> {
  const raw = await call<unknown>(`/notifications?page=${page}&size=${size}`);
  if (Array.isArray(raw)) return raw as NotificationItem[];
  if (
    raw &&
    typeof raw === "object" &&
    "content" in (raw as Record<string, unknown>)
  ) {
    return (raw as { content?: NotificationItem[] }).content ?? [];
  }
  return [];
}

export const notificationsApi = {
  /** Lista paginada — devuelve siempre un array (normalizado). */
  list: (page = 0, size = 20) => fetchNotificationsList(page, size),
  /** Conteo de no leídas (notification-service real). */
  unreadCount: () => call<{ count: number }>(`/notifications/unread-count`),
  /** Marca una notificación específica como leída. */
  read: (id: string) =>
    call<void>(`/notifications/${id}/read`, { method: "POST" }),
  /** Marca todas como leídas (alias retro-compat). */
  readAll: () =>
    call<{ updated: number; at: string }>(`/notifications/read-all`, {
      method: "POST",
    }),
  markAllRead: () => call<void>(`/notifications/read-all`, { method: "POST" }),
};

// ============ Project extensions: timeline + kanban — BLOCKER-P01..P03 ============
export interface ProjectTimelineEntry {
  id: string;
  projectId?: string;
  tenantId?: string;
  eventType?: string;
  sourceModule?: string;
  title?: string;
  description?: string | null;
  occurredAt?: string;
  at: string;
  actor: string;
  action: string;
  detail: string | null;
}
export interface ProjectReminderSettings {
  daysBefore: number[];
  assignmentNotificationsEnabled: boolean;
}
export interface KanbanColumn {
  id: string;
  titleKey?: string;
  title: string;
  documents: {
    id: string;
    projectId?: string;
    name: string;
    status?: string;
    kanbanColumn?: string;
    risk?: number;
    assignee?: string;
    updatedAt?: string;
  }[];
}
export const projectExtApi = {
  timeline: (projectId: string) =>
    call<PagedResponse<ProjectTimelineEntry>>(
      `/projects/${projectId}/timeline?size=50`,
    ).then((page) => page.content),
  kanban: (projectId: string) =>
    call<KanbanColumn[]>(`/projects/${projectId}/kanban`),
  moveDocument: (projectId: string, documentId: string, toColumnId: string) =>
    call<KanbanColumn[]>(
      `/projects/${projectId}/kanban/${documentId}`,
      putBody({ toColumnId }),
    ),
};

// ============ Workspace switcher — list user workspaces ============
export interface UserWorkspace {
  id: string;
  name: string;
  plan: string;
  role: string;
  tenantId: string;
}
export const workspacesApi = {
  mine: () => call<UserWorkspace[]>(`/auth/workspaces`),
  switch: (tenantId: string | null) =>
    call<TokenResponse>(`/auth/workspaces/switch`, jsonBody({ tenantId })).then(
      (tokens) => {
        applyTokenResponse(tokens);
        return tokens;
      },
    ),
};

// ============ MFA (TOTP RFC-6238) — identity-service /auth/mfa/* ============
export interface MfaEnrollResult {
  secret: string;
  otpauthUri: string;
  qrPngBase64: string;
  mfaPendingToken: string;
}
export const mfaApi = {
  /** Genera un secret TOTP nuevo y devuelve URI + QR base64 para escanear. */
  enroll: () => call<MfaEnrollResult>(`/auth/mfa/enroll`, { method: "POST" }),
  /** Confirma el enrolamiento validando el primer código del autenticador. */
  confirm: (mfaPendingToken: string, code: string) =>
    call<{ recoveryCodes: string[] }>(
      `/auth/mfa/verify-enrollment`,
      jsonBody({ mfaPendingToken, totpCode: code }),
    ),
  /** Regenera códigos de recuperación y los devuelve una sola vez. */
  recoveryCodes: () =>
    call<{ recoveryCodes: string[] }>(`/auth/mfa/recovery-codes/generate`, {
      method: "POST",
    }),
  /** Desactiva MFA y elimina el secret almacenado. */
  disable: (body: { password?: string; totpCode?: string }) =>
    call<void>(`/auth/mfa/disable`, jsonBody(body)),
};

// ============ WebAuthn passkeys (Sprint 59) — identity /auth/webauthn/* ============
export interface WebAuthnCredentialView {
  id: string;
  label: string | null;
  transports: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}
export interface WebAuthnRegisterStart {
  token: string;
  publicKey: CreationOptionsJSON;
}
export const webauthnApi = {
  /** Inicia el registro de una passkey (devuelve opciones + token de reto). */
  registerStart: () =>
    call<WebAuthnRegisterStart>(`/auth/webauthn/register/start`, {
      method: "POST",
    }),
  /** Confirma el registro enviando la attestation del autenticador. */
  registerFinish: (body: {
    token: string;
    attestationObject: string;
    clientDataJSON: string;
    label?: string | null;
    transports?: string | null;
  }) =>
    call<WebAuthnCredentialView>(
      `/auth/webauthn/register/finish`,
      jsonBody(body),
    ),
  /** Lista las passkeys del usuario autenticado. */
  list: () => call<WebAuthnCredentialView[]>(`/auth/webauthn/credentials`),
  /** Elimina una passkey por id. */
  remove: (id: string) =>
    call<void>(`/auth/webauthn/credentials/${id}`, { method: "DELETE" }),
};

// ============ Admin audit — audit-service /admin/audit/* ============
export interface AuditLogItem {
  id: string;
  eventTime: string;
  tenantId: string | null;
  actorUserId: string | null;
  actorEmail: string | null;
  requestId: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  sourceService: string;
  outcome: string;
  ipAddress: string | null;
}
export interface SystemErrorItem {
  id: string;
  eventTime: string;
  tenantId: string | null;
  requestId: string | null;
  sourceService: string | null;
  uri: string | null;
  httpMethod: string | null;
  errorCode: string | null;
  exceptionClass: string | null;
  message: string | null;
  severity: string;
}
export interface AuditTsaVerificationResult {
  valid: boolean;
  signedAt: string | null;
  signer: string | null;
  algorithm: string | null;
  reason: string | null;
}
export const adminAuditApi = {
  logs: (
    q: {
      tenantId?: string;
      actorId?: string;
      action?: string;
      resourceType?: string;
      from?: string;
      to?: string;
      page?: number;
      size?: number;
    } = {},
  ) => {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => {
      if (v !== undefined && v !== "") params.append(k, String(v));
    });
    return call<{
      content: AuditLogItem[];
      totalElements: number;
      totalPages: number;
    }>(`/admin/audit/logs?${params.toString()}`);
  },
  systemErrors: (
    q: {
      tenantId?: string;
      severity?: string;
      errorCode?: string;
      from?: string;
      to?: string;
      page?: number;
      size?: number;
    } = {},
  ) => {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => {
      if (v !== undefined && v !== "") params.append(k, String(v));
    });
    return call<{
      content: SystemErrorItem[];
      totalElements: number;
      totalPages: number;
    }>(`/admin/audit/system-errors?${params.toString()}`);
  },
  verifyTsa: (eventId: string) =>
    call<AuditTsaVerificationResult>(
      `/audit/${encodeURIComponent(eventId)}/verify-tsa`,
    ),
};

// ============ Project Tasks — project-service /tasks/* ============
export type ProjectTaskStatus =
  | "TODO"
  | "IN_PROGRESS"
  | "IN_REVIEW"
  | "BLOCKED"
  | "DONE"
  | "CANCELLED";
export type ProjectTaskPriority = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export interface ProjectTaskItem {
  id: string;
  tenantId: string;
  projectId: string;
  title: string;
  description: string | null;
  assigneeId: string | null;
  assigneeEmail: string | null;
  status: ProjectTaskStatus;
  priority: ProjectTaskPriority;
  dueDate: string | null;
  completedAt: string | null;
  relatedDocId: string | null;
  sortOrder: number;
  dueSoon?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const projectTasksApi = {
  list: (
    filters: {
      projectId?: string;
      assigneeId?: string;
      status?: ProjectTaskStatus;
      dueBefore?: string;
      page?: number;
      size?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== "") qs.append(k, String(v));
    });
    return call<{
      content: ProjectTaskItem[];
      totalElements: number;
      totalPages: number;
    }>(`/tasks?${qs.toString()}`);
  },
  get: (id: string) => call<ProjectTaskItem>(`/tasks/${id}`),
  create: (body: {
    projectId: string;
    title: string;
    description?: string;
    assigneeId?: string;
    assigneeEmail?: string;
    status?: ProjectTaskStatus;
    priority?: ProjectTaskPriority;
    dueDate?: string;
    relatedDocId?: string;
    sortOrder?: number;
  }) => call<ProjectTaskItem>(`/tasks`, jsonBody(body)),
  update: (
    id: string,
    body: Partial<{
      title: string;
      description: string;
      assigneeId: string;
      assigneeEmail: string;
      status: ProjectTaskStatus;
      priority: ProjectTaskPriority;
      dueDate: string;
      sortOrder: number;
    }>,
  ) => call<ProjectTaskItem>(`/tasks/${id}`, putBody(body)),
  remove: (id: string) => call<void>(`/tasks/${id}`, { method: "DELETE" }),
};

// ============ Workflows — workflow-service /workflows/* ============
export type WorkflowDefStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";
export type WorkflowRunStatus =
  | "RUNNING"
  | "WAITING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";
export type WorkflowTaskStatus =
  | "OPEN"
  | "CLAIMED"
  | "COMPLETED"
  | "CANCELLED"
  | "EXPIRED";
export type WorkflowUseCaseCode =
  | "CU-1"
  | "CU-2"
  | "CU-3"
  | "CU-4"
  | "CU-5"
  | "CU-6";
export type WorkflowFormFieldType =
  | "TEXT"
  | "NUMBER"
  | "BOOLEAN"
  | "DATE"
  | "ENUM";

export interface WorkflowDefinition {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  description: string | null;
  bpmnXml?: string;
  status: WorkflowDefStatus;
  version: number;
  deployedAt: string | null;
  slaMinutes: number | null;
  createdAt: string;
  updatedAt: string;
}
export type WorkflowDef = WorkflowDefinition;

export interface WorkflowRun {
  id: string;
  tenantId: string;
  definitionId: string;
  businessKey: string | null;
  status: WorkflowRunStatus;
  currentNode: string | null;
  startedBy: string;
  startedByEmail: string | null;
  startedAt: string;
  endedAt: string | null;
  slaDeadline: string | null;
  slaBreached: boolean;
  errorMessage: string | null;
  relatedProjectId?: string | null;
  relatedDocumentId?: string | null;
  timeline?: WorkflowTaskItem[];
  variables?: WorkflowVariableItem[];
}

export interface WorkflowVariableItem {
  id: string;
  runId: string;
  name: string;
  value: unknown;
}

export interface WorkflowTaskItem {
  id: string;
  tenantId: string;
  runId: string;
  nodeId: string;
  name: string;
  assigneeId: string | null;
  assigneeEmail: string | null;
  candidateRole: string | null;
  status: WorkflowTaskStatus;
  dueAt: string | null;
  slaBreached: boolean;
  slaBreachedAt: string | null;
  slaNotificationSentAt: string | null;
  claimedAt: string | null;
  completedAt: string | null;
  decision: string | null;
  notes: string | null;
  completedBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
  formFields?: WorkflowTaskFormField[];
}

export interface WorkflowTaskFormField {
  id: string;
  label: string;
  type: WorkflowFormFieldType;
  required: boolean;
  options?: unknown;
  defaultValue?: unknown;
}

export interface WorkflowTaskForm {
  taskId: string;
  runId: string;
  nodeId: string;
  fields: WorkflowTaskFormField[];
  values: Record<string, unknown>;
  submitted: boolean;
}

export interface WorkflowComment {
  id: string;
  tenantId: string;
  scope: "RUN" | "TASK" | string;
  runId: string;
  taskId: string | null;
  authorId: string;
  authorEmail: string | null;
  body: string;
  createdAt: string;
}

export interface WorkflowTemplate {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  description: string | null;
  useCaseCode: WorkflowUseCaseCode;
  bpmnXml: string;
  variablesSchema: Record<string, unknown> | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
}

export interface WorkflowConnectorDeadLetter {
  id: string;
  tenantId: string;
  runId: string;
  nodeId: string;
  connectorType: string;
  status: string;
  attempts: number;
  errorType: string | null;
  errorMessage: string | null;
  nextRetryAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowConnectorRetry {
  dlqId: string;
  runId: string;
  nodeId: string;
  status: string;
  retriedAt: string;
}

/** Habilidad de IA disponible para el conector `ai:` (catálogo S45-C1). */
export interface AiSkillOption {
  code: string;
  name: string;
  description: string;
}

export interface WorkflowBottleneckMetric {
  nodeId: string;
  nodeName: string;
  openTasks: number;
  slaBreaches: number;
  averageWaitSeconds: number;
}

export interface WorkflowProcessMetric {
  definitionId: string;
  definitionCode: string;
  definitionName: string;
  from: string | null;
  to: string | null;
  runsStarted: number;
  runsCompleted: number;
  averageCycleTimeSeconds: number;
  slaCompliancePercent: number;
  rejectionRatePercent: number;
  bottlenecks: WorkflowBottleneckMetric[];
}

type WorkflowPageDto<T> = Partial<PagedResponse<T>> & {
  content?: T[];
  items?: T[];
  total?: number;
  hasNext?: boolean;
};

function normalizeWorkflowPage<T>(page: WorkflowPageDto<T>): PagedResponse<T> {
  const content = page.content ?? page.items ?? [];
  const pageNumber = page.page ?? 0;
  const size = page.size ?? content.length;
  const totalElements = page.totalElements ?? page.total ?? content.length;
  const totalPages =
    page.totalPages ??
    (size > 0 ? Math.max(1, Math.ceil(totalElements / size)) : 0);
  return {
    content,
    items: page.items ?? content,
    page: pageNumber,
    size,
    totalElements,
    totalPages,
    first: page.first ?? pageNumber === 0,
    last:
      page.last ??
      (page.hasNext !== undefined
        ? !page.hasNext
        : pageNumber >= totalPages - 1),
  };
}

export const workflowsApi = {
  list: (status?: WorkflowDefStatus) =>
    call<WorkflowPageDto<WorkflowDefinition>>(
      `/workflows/definitions${buildQuery({ status })}`,
    ).then(normalizeWorkflowPage),
  create: (body: {
    code: string;
    name: string;
    description?: string;
    bpmnXml: string;
    slaMinutes?: number;
  }) => call<WorkflowDefinition>("/workflows/definitions", jsonBody(body)),
  validate: (bpmnXml: string) =>
    call<unknown>("/workflows/definitions/validate", jsonBody({ bpmnXml })),
  get: (id: string) => call<WorkflowDefinition>(`/workflows/definitions/${id}`),
  getXml: async (id: string) => {
    const definition = await call<WorkflowDefinition>(
      `/workflows/definitions/${id}`,
    );
    return definition.bpmnXml ?? "";
  },
  saveXml: async (id: string, xml: string) => {
    const current = await call<WorkflowDefinition>(
      `/workflows/definitions/${id}`,
    );
    return call<WorkflowDefinition>(
      `/workflows/definitions/${id}`,
      putBody({
        name: current.name,
        description: current.description,
        bpmnXml: xml,
        activate: false,
        slaMinutes: current.slaMinutes,
      }),
    );
  },
  update: (
    id: string,
    body: {
      name: string;
      description?: string | null;
      bpmnXml: string;
      slaMinutes?: number | null;
      activate?: boolean;
    },
  ) => call<WorkflowDefinition>(`/workflows/definitions/${id}`, putBody(body)),
  archive: (id: string) =>
    call<WorkflowDefinition>(`/workflows/definitions/${id}`, del()),
  aiSkills: () => call<AiSkillOption[]>("/workflows/catalog/ai-skills"),
  deploy: async (id: string) => {
    // NO ELIMINAR — fix P14H · deploy real usa endpoint dedicado del motor workflow
    return call<WorkflowDefinition>(`/workflows/definitions/${id}/deploy`, {
      method: "POST",
    });
  },
  startRun: (
    id: string,
    body?: {
      businessKey?: string;
      projectId?: string;
      documentId?: string;
      variables?: Record<string, unknown>;
    },
  ) =>
    call<WorkflowRun>(
      `/workflows/runs`,
      jsonBody({ definitionId: id, ...body }),
    ),
  startRunByCode: (
    definitionCode: string,
    body?: {
      businessKey?: string;
      projectId?: string;
      documentId?: string;
      variables?: Record<string, unknown>;
    },
  ) =>
    call<WorkflowRun>(`/workflows/runs`, jsonBody({ definitionCode, ...body })),
  listRuns: (definitionId: string, status?: WorkflowRunStatus) => {
    return call<WorkflowPageDto<WorkflowRun>>(
      `/workflows/runs${buildQuery({ definitionId, status })}`,
    ).then(normalizeWorkflowPage);
  },
  getRun: (runId: string) => call<WorkflowRun>(`/workflows/runs/${runId}`),
  runs: (
    params:
      | string
      | {
          definitionId?: string;
          projectId?: string;
          status?: WorkflowRunStatus;
          page?: number;
          size?: number;
        },
  ) => {
    const filters =
      typeof params === "string" ? { definitionId: params } : params;
    return call<WorkflowPageDto<WorkflowRun>>(
      `/workflows/runs${buildQuery({
        definitionId: filters.definitionId,
        projectId: filters.projectId,
        status: filters.status,
        page: filters.page ?? 0,
        size: filters.size ?? 50,
      })}`,
    ).then(normalizeWorkflowPage);
  },
  xml: (id: string) => workflowsApi.getXml(id),
  cancelRun: (runId: string, reason?: string) => {
    const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    return call<WorkflowRun>(`/workflows/runs/${runId}/cancel${qs}`, {
      method: "POST",
    });
  },
  listTasks: (
    filters: {
      runId?: string;
      assigneeId?: string;
      status?: WorkflowTaskStatus;
      assigneeMode?: "mine" | "available" | "all";
      page?: number;
      size?: number;
    } = {},
  ) =>
    call<WorkflowPageDto<WorkflowTaskItem>>(
      `/workflows/tasks${buildQuery({
        runId: filters.runId,
        assigneeId: filters.assigneeId,
        status: filters.status,
        assigneeMode: filters.assigneeMode,
        page: filters.page ?? 0,
        size: filters.size ?? 50,
      })}`,
    ).then(normalizeWorkflowPage),
  claim: (taskId: string) =>
    call<WorkflowTaskItem>(`/workflows/tasks/${taskId}/claim`, {
      method: "POST",
    }),
  reassign: (
    taskId: string,
    body: {
      assigneeId?: string | null;
      candidateRole?: string | null;
      reason?: string;
    },
  ) =>
    call<WorkflowTaskItem>(
      `/workflows/tasks/${taskId}/reassign`,
      jsonBody(body),
    ),
  taskForm: (taskId: string) =>
    call<WorkflowTaskForm>(`/workflows/tasks/${taskId}/form`),
  submitTaskForm: (taskId: string, values: Record<string, unknown>) =>
    call<WorkflowTaskForm>(
      `/workflows/tasks/${taskId}/form`,
      jsonBody({ values }),
    ),
  complete: (
    taskId: string,
    decision?: string,
    notes?: string,
    variables?: Record<string, unknown>,
  ) =>
    call<WorkflowTaskItem>(
      `/workflows/tasks/${taskId}/complete`,
      jsonBody({ decision, notes, variables }),
    ),
  taskComments: (taskId: string, page = 0, size = 20) =>
    call<WorkflowPageDto<WorkflowComment>>(
      `/workflows/tasks/${taskId}/comments${buildQuery({ page, size })}`,
    ).then(normalizeWorkflowPage),
  addTaskComment: (taskId: string, body: string) =>
    call<WorkflowComment>(
      `/workflows/tasks/${taskId}/comments`,
      jsonBody({ body }),
    ),
  runComments: (runId: string, page = 0, size = 20) =>
    call<WorkflowPageDto<WorkflowComment>>(
      `/workflows/runs/${runId}/comments${buildQuery({ page, size })}`,
    ).then(normalizeWorkflowPage),
  addRunComment: (runId: string, body: string) =>
    call<WorkflowComment>(
      `/workflows/runs/${runId}/comments`,
      jsonBody({ body }),
    ),
  templates: (
    params: {
      useCaseCode?: WorkflowUseCaseCode;
      page?: number;
      size?: number;
    } = {},
  ) =>
    call<WorkflowPageDto<WorkflowTemplate>>(
      `/workflows/templates${buildQuery({
        useCaseCode: params.useCaseCode,
        page: params.page ?? 0,
        size: params.size ?? 50,
      })}`,
    ).then(normalizeWorkflowPage),
  createTemplate: (body: {
    code: string;
    name: string;
    description?: string;
    useCaseCode: WorkflowUseCaseCode;
    bpmnXml: string;
    variablesSchema?: Record<string, unknown> | null;
    tags?: string[];
  }) => call<WorkflowTemplate>("/workflows/templates", jsonBody(body)),
  getTemplate: (templateCode: string) =>
    call<WorkflowTemplate>(`/workflows/templates/${templateCode}`),
  createDefinitionFromTemplate: (
    templateCode: string,
    body: {
      code: string;
      name: string;
      description?: string;
      variables?: Record<string, unknown>;
      deploy?: boolean;
    },
  ) =>
    call<WorkflowDefinition>(
      `/workflows/templates/${templateCode}/definitions`,
      jsonBody(body),
    ),
  deadLetters: (
    params: {
      runId?: string;
      status?: string;
      page?: number;
      size?: number;
    } = {},
  ) =>
    call<WorkflowPageDto<WorkflowConnectorDeadLetter>>(
      `/workflows/connectors/dead-letter${buildQuery({
        runId: params.runId,
        status: params.status,
        page: params.page ?? 0,
        size: params.size ?? 20,
      })}`,
    ).then(normalizeWorkflowPage),
  retryDeadLetter: (dlqId: string, reason?: string) =>
    call<WorkflowConnectorRetry>(
      `/workflows/connectors/dead-letter/${dlqId}/retry`,
      jsonBody({ reason }),
    ),
  processMetrics: (
    params: {
      definitionId?: string | null;
      from?: string;
      to?: string;
      page?: number;
      size?: number;
    } = {},
  ) =>
    call<WorkflowPageDto<WorkflowProcessMetric>>(
      `/workflows/metrics/processes${buildQuery({
        definitionId: params.definitionId,
        from: params.from,
        to: params.to,
        page: params.page ?? 0,
        size: params.size ?? 20,
      })}`,
    ).then(normalizeWorkflowPage),
  sweepSla: () =>
    call<{ breached: number; notificationsQueued: number }>(
      "/admin/workflows/sla/sweep",
      { method: "POST" },
    ),
};

// ============ Signature — signature-service /signature/* ============
export type SignatureRequestStatus =
  | "DRAFT"
  | "SENT"
  | "SIGNED"
  | "COMPLETED"
  | "DECLINED"
  | "CANCELLED"
  | "EXPIRED"
  | "FAILED";

export interface SignatureRequestItem {
  id: string;
  tenantId: string;
  documentId: string;
  provider: string;
  externalId: string | null;
  subject: string;
  message: string | null;
  status: SignatureRequestStatus;
  initiatedBy: string;
  sentAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface SignatureSignerInput {
  email: string;
  name: string;
  role?: string;
  routingOrder?: number;
}

export interface CreateSignatureRequest {
  documentId: string;
  documentVersionId?: string;
  subject: string;
  message?: string;
  signers: SignatureSignerInput[];
  callbackUrl?: string;
}

export const signatureApi = {
  list: (page = 0, size = 20, status?: SignatureRequestStatus) => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(size));
    if (status) params.set("status", status);
    return call<{
      content: SignatureRequestItem[];
      totalElements: number;
      totalPages: number;
    }>(`/signature/requests?${params.toString()}`);
  },
  get: (id: string) => call<SignatureRequestItem>(`/signature/requests/${id}`),
  create: (body: CreateSignatureRequest) =>
    call<SignatureRequestItem>(`/signature/requests`, jsonBody(body)),
  cancel: (id: string, reason?: string) => {
    const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    return call<SignatureRequestItem>(`/signature/requests/${id}/cancel${qs}`, {
      method: "POST",
    });
  },
};
